import * as SentryBrowser from '@sentry/browser';
import { Workbox } from 'workbox-window';
import {
    EquipmentCollection,
    MobileIngredients
} from '../backbone/features/mobileForm/ingredientEquipmentTemperatureFields/models';
import {
    FormDraft,
    MobileFormStructure,
    MobileSubmission
} from '../backbone/features/mobileForm/MobileFormStructure/models';
import { FollowUpAction } from '../backbone-unorganized/followUpActions/models';
import { MobileFormPage, SubmittedFormPage } from '../backbone/features/mobileForm/views';
import { UploadableFile, ZenputFile, ZenputFiles } from '../backbone/shared/models/files';
import { MobileNotificationView } from '../backbone/shared/views/MobileNotificationView';
import { NotificationsPage } from './comments/views';
import { getDefaultLocationDashboardTabId, MobileDashboard } from './dashboard/views';
import { Djangosaml2ClientAuth } from './djangosaml2/djangosaml2ClientAuth';
import {
    FastTasksLocal_record,
    FormDrafts,
    FormTemplatesLocal_record,
    NewProjects,
    NewTasks,
    PendingFormSubmissions,
    PendingTasks,
    ProcessingTasks
} from './local_records';

import { ActiveCompany, ActiveUser, LocationsAttributes, MobileAccounts } from './models';
import { SensorDetailPage } from './sensors/views';
import { UnreadCount } from './shared/comments/models';
import $f, { Account, Paginator, UserDevice, ZenputStorage } from './shared/forms';
import { Sensor, SensorSparkline } from './shared/sensors/models';
import { FastTasks, MobileProject, MobileTask, MobileTasks, MyTasks } from './tasks/models';
import { FastTasksPage } from './tasks/views';
import { createUUID, removeLocalFile } from './utils/files';
import localization from './utils/localization';
import { setupLogging } from './utils/logging/index';
import { getSamlJwtFromDeepLink } from './utils/sso';
import {
    FollowUpActionDetailsPage,
    FormTemplatesPage,
    LoginForm,
    MobileAppLayout,
    MobileUserProfileSettings,
    NotificationSettings,
    OiaDialog,
    ProjectsView,
    StatusPage,
    TaskDetailsPage,
    TasksPage
} from './views';
import { AVAILABLE_LOCALES } from '../constants';
import { CordovaPlugins } from '../shared/CordovaPlugins';
import { initializePushNotifications, unregisterDevice } from './utils/pushNotifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';
import {
    confirmNotification,
    hideLoadingDialog,
    showOfflineMessage,
    showUploadProgress,
    showBeginningUploadMessage,
    showSuccessMessage,
    showLoadingDialog,
    showErrorMessage,
    scheduleTaskDueSoonLocalNotifications
} from '../shared/utils/notifications';
import { isNative, isWeb, isAndroid, isIos, isWindows, isConnected, isLoggedIn, isInIframe } from '../shared/utils/is';
import { checkForUpdate, compareVersions } from '../shared/utils/version';
import {
    enableWeinreRemoteDebug,
    getDebuggingFlag,
    isDebuggingEnabled,
    isReleaseBuild
} from '../shared/utils/debugging';
import { NewVersionUpdate, showUpdatePrompt } from '../react/features/NewVersionUpdate';
import { ConfirmNotification } from '../react/features/ConfirmNotification';
import { initializePushNotificationsForPWA, unregisterDeviceForPWA } from './utils/firebase/pushNotifications';
import { DeploymentFlagProvider } from '../shared/deploymentFlags';

const MAXIMUM_LOCATIONS = 10;

const metaTag = document.querySelector('meta[name="is-pwa-enabled"]');
const isPwaEnabled = metaTag ? metaTag.getAttribute('content') === 'true' : false;

const hoursToMilliseconds = (hours) => 1000 * 60 * 60 * hours;

if (isPwaEnabled) {
    if ('serviceWorker' in navigator && !isInIframe()) {
        window.addEventListener('load', () => {
            const wb = new Workbox('/mobile/sw.js');

            setInterval(() => {
                wb.update();
            }, hoursToMilliseconds(23));

            if (!isReleaseBuild()) {
                const frequencyMetaTag = document.querySelector('meta[name="update-check-frequency"]');
                const pwaUpdateCheckFrequency = frequencyMetaTag
                    ? Number.parseInt(frequencyMetaTag.getAttribute('content'), 10)
                    : -1;
                if (pwaUpdateCheckFrequency !== -1) {
                    setInterval(() => {
                        wb.update();
                    }, pwaUpdateCheckFrequency);
                }
            }

            setInterval(async () => {
                const sw = await wb.getSW();

                if (sw.state === 'installed' || sw.state === 'redundant') {
                    showUpdatePrompt(wb);
                }
            }, hoursToMilliseconds(24));

            wb.addEventListener('waiting', () => {
                showUpdatePrompt(wb);
            });

            wb.addEventListener('controlling', () => {
                window.location.reload();
            });

            wb.addEventListener('installed', async () => {
                const registration = await navigator.serviceWorker.getRegistration();
                if (!registration) {
                    return;
                }
                const multipleSW = registration.active !== null && registration.waiting !== null;

                if (!multipleSW) {
                    showLoadingDialog();
                }
            });

            wb.addEventListener('redundant', async () => {
                // Something went wrong during the service worker installation
                const registration = await navigator.serviceWorker.getRegistration();
                if (!registration) {
                    return;
                }
                const multipleSW = registration.active !== null && registration.waiting !== null;

                if (!multipleSW) {
                    hideLoadingDialog();
                }
            });

            wb.register();
        });
    }
}

export const $f_window = {
    viewport: null,
    last_paused_ts: null,
    uploading: false,
    last_rdbms_id: null, // this global is needed for mobile web.
    has_resumed: false, // for redirection (@see window.handleOpenURL and $f.mobile.redirect())
    is_paused: false // detect if the app is currently paused or in the foreground
};

const mobile = {
    settings: {},
    is: {},
    models: {
        mixins: {}
    },
    collections: {
        mixins: {}
    },
    views: {
        mixins: {}
    },
    dashboard: null, // Defined in dashboard/dashboard.js
    tasks: null, // Defined in tasks/tasks.js
    notifications: {}, // alerts, popups, etc //// REMOVE
    local_records: {
        mixins: {}
    },
    nav: {}, // functions for us to manage our page history
    uploads: {}, // for starting pending_*_uploads, calculating upload progress, etc.

    localization: {},
    bluetooth: {},

    keyboard_height: 300, // estimate for height of keyboard (will be set by ionic keyboard module)
    is_initialized: false, // lets us prevent a call to logout if ajax returns an error
    // before the app is initialized

    // We will set the default Page for the app here, since we need to import it dynamically
    default_start_page_class: MobileDashboard,

    DELAY_ON_ORIENTATION_CHANGE_MS: 300, // how long we'll delay the 'app:orientation_change' event,
    // necessary to account for device rotation, etc.

    /**
     * Get the default distribute group for active user.
     *
     * @return {object} The id and name of the user's default distribute group if it has one root.
     */
    get_active_user_distribute_group: function () {
        var active_user_distribute_group = {};

        var default_distribute_group = $f.active_user.get('default_distribute_group');
        if ($f.active_user.get('has_only_one_root') && default_distribute_group) {
            active_user_distribute_group = default_distribute_group;
        } else {
            active_user_distribute_group.id = null;
            active_user_distribute_group.name = 'All';
        }

        return {
            id: active_user_distribute_group.id,
            display_value: active_user_distribute_group.name
        };
    },

    /**
     * Android/Windows Back Button integration
     *
     * The android back button is tied to our HTML back button, as well as any element with the
     * class `android_back_action` (e.g., the close button on the image previewer). If neither
     * of these can be found, the back button will close the app.
     * @return {boolean}     True if a back action was taken, false if not and we should let the OS decide
     */
    _tryOnBackNative: function () {
        if ($('.main_content.velocity-animating').length) {
            return true;
        }

        try {
            if ($('iframe#webWidget').length > 0) {
                window.zE.hide();
                return true;
            }

            if ($('.wootric-modal-showing #wootric-x').length > 0) {
                // wootric survey x button
                $('.wootric-modal-showing #wootric-x').trigger('click');
                return true;
            }
            if ($('.mbsc-fr-btn-c > div').length > 0) {
                // mobiscroll dialog cancel
                $('.mbsc-fr-btn-c > div').trigger('click');
                return true;
            }
            if ($('.android_back_action').length > 0) {
                // any custom elements that want back functionality
                $('.android_back_action').trigger('click');
                return true;
            }
            if ($f.application.content.currentView.r_leftmenu.currentView.is_open()) {
                $f.application.vent.trigger('mobile:close_leftmenu');
                return true;
            }
            if ($('#content_overlay:not(.stale) #action_left.go_back').length) {
                // modal back button
                $('#content_overlay:not(.stale) #action_left.go_back').trigger('click');
                return true;
            }
            if ($('.main_content:not(.stale) #action_left.go_back').length > 0) {
                // main content back button
                $('.main_content:not(.stale) #action_left.go_back').trigger('click');
                return true;
            }

            if ($('.main_content:not(.stale) .app_bar_go_back').length > 0) {
                // react AppBar back button
                $('.main_content:not(.stale) .app_bar_go_back').trigger('click');
                return true;
            }

            return false;
        } catch (e) {
            return false;
        }
    },

    _initializeMobileStorage: async function () {
        /**
         *
         * Phonegap will listen for any zenput:// links in the OS and alert us here. For a discussion and diagram
         * of how this process works, see Pull Request https://github.com/zenput/zenput/pull/958.
         *
         * Some things to note:
         *
         * - If the app is starting from scratch, both the default route and this listener will occur, causing a
         * painful race condition. We deal with this by setting a #hash on the page here, then doing a full reload so
         * the Backbone router can know to show the specified page.
         *
         * - Whenever our page is reloaded, this zenput:// event will re-fire. To address this, we ignore any events when
         * our page already has a #hash.
         *
         * - JS will not trigger a hash change/route change if the location.hash is changed to the already current #hash. To
         * address this, we first redirect to #noop, then whatever #hash we're aiming for:
         *
         *      location.hash = location.hash // won't do anything
         *      $f.utils.$f.utils.remove_url_hashbangs(); location.hash = location.hash // still won't do anything
         *
         *      // Works; our solution:
         *      $(window).on('hashchange', function(e) { // wait for #noop to apply
         *          $f.window.app_router.navigate(new_hash, true);
         *      }
         *      window.location.hash = 'noop';
         *
         */
        await $f.mobile.storage.initialize();

        if (isWeb()) {
            return;
        }

        window.addEventListener('native.keyboardshow', (event) => {
            $f.mobile.keyboard_height = event.keyboardHeight;
            $f.application.vent.trigger('cordova:keyboard:show');
        });

        window.addEventListener('native.keyboardhide', () => {
            $f.application.vent.trigger('cordova:keyboard:hide');
        });

        if (isWindows()) {
            window.handleOpenURL = (url) => {
                $f.mobile.redirect(url);
            };
        } else {
            App.addListener('appUrlOpen', (event) => {
                $f.mobile.redirect(event.url);
            });
        }

        document.addEventListener(
            'pause',
            () => {
                $f.window.last_paused_ts = new Date();
                if (!$f.application || !$f.application.vent) {
                    return;
                } // should never happen unless we're exiting right when we launch
                $f.application.vent.trigger('mobile:close_leftmenu');

                $f.window.is_paused = true;

                // Technically this should be done in appResumed, but we can't be sure that will fire before
                // the urlLoaded event, so we it as we pause for when we resume:
                $f.window.has_resumed = true;
                $(window).off('hashchange.zp_redirector'); // just to be safe, in case this wasn't done earlier at some point

                // on Android, usage of the BLE plugin puts the webview into the background momentarily for some reason,
                // so we can't just disconnect immediately. Instead, let's wait a few and see if we're still minimized
                if (isAndroid()) {
                    setTimeout(function () {
                        if (!$f.window.is_paused) {
                            return;
                        }
                        $f.mobile.bluetooth.disconnect();
                    }, 1000);
                } else {
                    $f.mobile.bluetooth.disconnect();
                }
            },
            false
        );

        document.addEventListener(
            'resume',
            () => {
                $f.window.is_paused = false;

                // get_device_location() will 'pause' and 'resume' the app when the permisison dialog is shown on Android.
                // We do not want to proceed if that's the case to avoid an infinite loop.
                if ($f.mobile.geolocation_in_progress) {
                    return;
                }

                $f.mobile.set_viewport();
                checkForUpdate();
                if (!$f.active_user || !$f.active_user.is_logged_in()) {
                    return;
                }

                // our internal function that find the current view and checks for
                // and view that would like to have a function fire upon resume
                $f.mobile.resume();
                $f.mobile.update_cache();

                if (isWeb()) {
                    initializePushNotificationsForPWA();
                } else {
                    if ($f.flags.get_flag('push_registration_refactor')) {
                        initializePushNotifications();
                    } else {
                        $f.mobile.push_notifications.initialize();
                    }
                }

                // Get timezone from user device and update in db
                $f.mobile.settings.set_user_time_zone();

                // check to see if the app was breifly paused, i.e for a photo, etc.
                // if so, ignore them. otherwise, lets try and upload anything in their queue
                if (new Date() - $f.window.last_paused_ts < 30000) {
                    return;
                } // 5 minutes

                if (isConnected() && $f.mobile.uploads.get_num_pending_submission_uploads() > 0) {
                    // Only start uploads on resume if the active collection is NOT a draft. If a draft is being
                    // worked on as it's uploaded, the upload process may modify some data on disk that conflicts
                    // with the active submission (e.g., image files --> values). @see @jira:3860
                    //
                    // Someday, it may be best to skip just the specific draft upload, but that's currently
                    // difficult without trigggering an "error."
                    if (!$f.mobile.form_drafts.is_active_collection_pending_draft()) {
                        $f.mobile.uploads.start_upload();
                    }
                }
            },
            false
        );

        /**
         * Device specific things
         */
        if (isAndroid()) {
            document.addEventListener(
                'backbutton',
                () => {
                    if (this._tryOnBackNative()) {
                        return;
                    }
                    navigator.app.exitApp();
                },
                false
            );
        } else if (isIos()) {
            CordovaPlugins().Keyboard.hideKeyboardAccessoryBar(false);
            if (compareVersions(CordovaPlugins().device.version, '7.0') >= 0) {
                $('html').addClass('fullscreen');
            }
        } else if (isWindows()) {
            Windows.UI.Core.SystemNavigationManager.getForCurrentView().onbackrequested = (e) => {
                e.handled = this._tryOnBackNative();
            };
        }
    },

    /**
     * Setup phonegap stuff for mobile specific platforms
     * such things at custom url schemes, android back buttons, etc.
     * Must be called before init().
     */
    preInit: async function () {
        window.onresize = $f.mobile.set_viewport;

        await this._initializeMobileStorage();
    },

    /**
     * This should be called before our application is first started.  It is also called
     * right after a user logs in.  It sets up our basic cached LocalRecords as well
     * as a few UI things.
     */
    init: async function (callback) {
        $f.mobile.set_viewport();

        var continue_init = function () {
            setupLogging();

            /*
             * Setup our localStorage caches.  Be sure to call clear() in the
             * $f.mobile.logout method where appropriate
             */
            $f.mobile.form_templates = new FormTemplatesLocal_record({
                klass: MobileFormStructure,
                cache_key: 'rdbms_id',
                comparator: 'title',
                ttl: 0
            });
            $f.mobile.form_drafts = new FormDrafts({ klass: FormDraft, cache_key: 'draft_id', ttl: 0 });
            $f.mobile.pending_form_submissions = new PendingFormSubmissions({
                klass: MobileFormStructure,
                cache_key: 'pending_form_submission_id',
                comparator: 'title',
                ttl: 0
            });
            $f.mobile.pending_task_submissions = new PendingTasks({
                klass: MobileTask,
                cache_key: 'pending_task_submission_id'
            });
            $f.mobile.pending_tasks_in_processing = new ProcessingTasks({
                klass: MobileTask,
                cache_key: 'pending_tasks_in_processing_id'
            });
            $f.mobile.new_tasks = new NewTasks({ klass: MobileTask, cache_key: 'new_task_id' });
            $f.mobile.new_project = new NewProjects({ klass: MobileProject, cache_key: 'new_project_id' });
            $f.mobile.fast_tasks = new FastTasksLocal_record({ klass: FastTasks, cache_key: 'fast_tasks_id' });

            $f.mobile.unread_notifications_count = new UnreadCount();

            $f.mobile.update_cache(true);
            if (isWeb()) {
                initializePushNotificationsForPWA();
            } else {
                if ($f.flags.get_flag('push_registration_refactor')) {
                    initializePushNotifications();
                } else {
                    $f.mobile.push_notifications.initialize();
                }
            }
            $f.mobile.bluetooth.initialize();
            $f.mobile.dashboard.initialize();
            $f.mobile.tasks.initialize();
            $f.mobile.form_templates.get_user_templates();

            $f.mobile.settings.set_user_time_zone();

            // unfortunately we are still setting this all over the place.  this is due to the Backbone Bug
            // where the 'change' event wasnt firing.  Search for @note:change_all to see more instances/info
            $f.application.vent.trigger('mobile:set_num_uploads');

            // Remove old "recent forms" and "account" cache items. Added May 2016; safe to remove after a few months
            $f.mobile.cache.clear_by_substring(['recent_rdbms_id', 'account_tag_id']);

            $f.mobile.is_initialized = true;
            callback();
        };

        $f.active_user = new ActiveUser();
        $f.active_company = new ActiveCompany();
        $f.flags = DeploymentFlagProvider;
        await DeploymentFlagProvider.initialize();
        if ($f.flags.get_flag('rebrand')) {
            document.title = 'Crunchtime Ops Execution';
        }
        $f.mobile.settings.patch_user_agent(function () {
            $f.storage = new ZenputStorage();

            isLoggedIn(
                () => {
                    $f.active_user.fetch({
                        success: function () {
                            $f.active_company.fetch({
                                success: function () {
                                    var _continue = function () {
                                        $f.mobile.uploads.load_pending_uploads(function () {
                                            $f.mobile.uploads.upload_pending_files(true);
                                            if ($f.active_user.get('local_notifications_enabled')) {
                                                let my_tasks = new MyTasks([], {
                                                    paginator: new Paginator({ filter: 'me', status: 'open' })
                                                });
                                                my_tasks.fetch({ remove: false });
                                            }
                                            continue_init();
                                        });
                                    };

                                    if ($f.active_user.is_logged_in()) {
                                        $f.storage.refresh({
                                            complete: _continue
                                        });
                                    } else {
                                        _continue();
                                    }
                                },
                                error: continue_init
                            });
                        },
                        error: continue_init // user is logged out
                    });
                },
                () => {
                    continue_init();
                },
                true
            );
        });
    },

    update_cache: async function (force) {
        if (!$f.active_user.is_logged_in()) {
            return;
        }

        await $f.mobile.pending_tasks_in_processing.clear_stale_records();

        var options = { force: force };
        $f.active_user.fetch(options);
        $f.active_company.fetch(
            _.extend({}, options, {
                success: function () {
                    $f.mobile.unread_notifications_count.fetch();
                }
            })
        );

        // Cache 2.0
        // We will disable cache_merge_on_fetch for this fetch to clear out deleted accounts (hotfix for @jira:REPT-446)
        new MobileAccounts([], { cache_merge_on_fetch: false }).fetch({
            success: function (accounts) {
                const location_id = accounts ? _.pluck(accounts.slice(0, MAXIMUM_LOCATIONS), 'id') : '';
                new MobileIngredients([], { location_id, cache_merge_on_fetch: false }).fetch({ force: true }); // Force to fetch from server
                new EquipmentCollection([], { location_id, cache_merge_on_fetch: false }).fetch({ force: true }); // Force to fetch from server
            }
        });

        // @note @author:david not updating dashboard cache here, since 90% of the time
        // it will fetch as part of the first view the user sees within the next couple seconds.

        // Fetch up to 100 nearest locations and put them on cache
        new LocationsAttributes().fetch();
    },

    /**
     * This is how we kick off our application during $(document).ready, right after
     * $f.mobile.init() has be run.  Unlike init, this should only ever be called once.
     *
     */
    start: function () {
        // setup DOM so we can hide and show features based on CSS rules
        if (isAndroid()) {
            $('html').addClass('android');

            if (isNative()) {
                // Try to detect bad Samsung keyboards
                if (CordovaPlugins().device.platform) {
                    var man = CordovaPlugins().device.manufacturer;
                    $('html').addClass(man);
                    if (man.indexOf('samsung') > -1) {
                        var version = CordovaPlugins().device.version;
                        if (compareVersions(version, '4.3') >= 0) {
                            $('html').addClass('fix_numeric_keyboard');
                        }
                    }
                }

                // Unfocusing the active input will tell our on_orientation_change that the keyboard has been closed. This happens
                // by default on iOS. (Not relying directly on this since we can't use it in for mobile web.)
                window.addEventListener('native.keyboardhide', function () {
                    $(':focus').blur();
                });
            }
        } else if (isIos()) {
            $('html').addClass('ios');

            if (isNative()) {
                CordovaPlugins().Keyboard.disableScroll(true);
            }
        } else if (isWindows()) {
            $('html').addClass('windows');

            // @author:bruce Fix for Windows Surface generating click on mouse up:
            // If you mouse down on element A, then move outside element A's region, mouse up generates
            // a click event (not according to spec and only happens with touch).
            // Tracing relevant events revealed that mouse/pointer out/over events aren't generated
            // until mouse move ends when using touch. The code below corrects for this by
            // manually updating focus when the mouse moves.
            document.addEventListener('pointermove', function (event) {
                var realTarget = document.elementFromPoint(event.clientX, event.clientY);
                if (realTarget && realTarget != event.target) {
                    realTarget.setPointerCapture(event.pointerId);
                }
            });
        }

        LocalNotifications.addListener(
            'localNotificationActionPerformed',
            $f.mobile.notifications.onLocalNotificationActionPerformed
        );

        // detects on orientation change while text area or input box is selected
        if (isAndroid() || isIos()) {
            $(document).on('focusin focusout', function (event) {
                if (!$(event.target).is('input, textarea')) {
                    return;
                }
                $f.mobile.set_viewport();
            });
        }

        if (isWeb() && !getDebuggingFlag('css_native')) {
            $('html').addClass('web');
        }

        // Since web can't handle pending uploads, lets just clear
        if (isWeb() && !getDebuggingFlag('pending')) {
            $f.mobile.uploads.for_each_pending_upload_structure(function (submissions) {
                // don't clear local copies on the web for form drafts
                if (submissions.keep_records) {
                    return;
                }
                submissions.clear_all();
            });
        }

        $f.application.on('initialize:after', function () {
            if (Backbone.history) {
                Backbone.history.start();
            }
        });

        $f.application.addInitializer(function (options) {
            $f.window.app_router = new Router();
        });

        i18next.init(
            {
                // @author:david i18next is adding the current locale to this array, even if we don't support it. So,
                // we're cloning it. Bad i18next!
                whitelist: _.clone(AVAILABLE_LOCALES),
                fallbackLng: localization.DEFAULT_LOCALE,
                resources: $f.i18n,
                load: 'languageOnly'
            },
            function (t) {
                /**
                 * Shortcut i18n.t() to window._t() for easy access. Unfortunately i18next doesn't handle empty
                 * translations for non-english (i.e., keys that have a translation of just "") so we need to
                 * handle that manually, here.
                 *
                 * @todo perhaps we should remove these keys from i18n-translations.js via scripts/process_translations.py
                 * someday as well, but for now this is easier.
                 */
                window._t = function (phrase, options) {
                    options = options || {};

                    var translation = i18next.t(phrase, options);

                    if (translation) {
                        return translation;
                    }

                    // If we haven't yet, try to use the default (English) translation.
                    // (For some reason, fallbackLng above isn't doing this for us.)
                    if (!options.lng) {
                        options.lng = localization.DEFAULT_LOCALE;
                        return _t(phrase, options);
                    }

                    // No translation available, just grab the english version manually.
                    // (This won't work with interpolation, but it should never happen.)
                    $f.mobile.logging.log_error(
                        'Unable to find translation, manually fetching from translations file',
                        {
                            translation_key: phrase
                        }
                    );

                    return $f.i18n.en_US.translation[phrase];
                };

                /**
                 * This is a temporary fix to translation of relative date strings. When we translate the webapp, this will
                 * not be necessary, but it is now because $f.utils.get_relative_date() is shared with webapp code. To handle
                 * this, we'll just override it with our own translation on top.
                 */
                var _get_relative_date = $f.utils.get_relative_date;
                $f.utils.get_relative_date = function () {
                    var answer = _get_relative_date.apply(this, arguments);

                    if (answer.label === 'Tomorrow') {
                        answer.label = _t('mobile_tasks_toolbarnav_header_tomorrow');
                    } else if (answer.label === 'Yesterday') {
                        answer.label = _t('mobile_tasks_toolbarnav_header_yesterday');
                    } else if (answer.label === 'Today') {
                        answer.label = _t('mobile_tasks_toolbarnav_header_today');
                    }

                    return answer;
                };

                $f.application.start();
            }
        );

        setTimeout(checkForUpdate, 2000);

        if (isWeb()) {
            const newVersionElem = document.getElementsByClassName('new_pwa_version')[0];

            window.mountReactJSX(newVersionElem, () => (
                <NewVersionUpdate
                    onConfirm={(wb) => {
                        wb.messageSkipWaiting();
                    }}
                    _t={_t}
                />
            ));
            const confirmNotificationElem = document.getElementsByClassName('confirm_notification')[0];

            window.mountReactJSX(confirmNotificationElem, () => <ConfirmNotification />);
        }
    },

    /**
     * Called immediatly after login.  Keep in mind, most things should
     * called in the $f.mobile.init()
     */
    login: function () {
        $f.mobile.settings.update_last_login(function () {
            // Rather than calling init(), update_cache(), or init_app_layout() directly here, we'll just "refresh" so everything
            // goes through the same code path (via the router) and any URL hashes are respected.
            window.location.reload();
        });
    },

    /**
     * Logs a user out and shows the login screen by default
     *
     * @param redirect - If set to false, the user won't be redirected to the LoginForm
     * @returns {boolean}
     */
    logout: function (redirect) {
        redirect = typeof redirect !== 'undefined' ? redirect : true;

        // Make sure our iOS badge is set back to 0. The badge persists between installs, so it's important to do this
        // regardless of whether or not the user was previously logged in.
        $f.mobile.unread_notifications_count.reset();

        $f.mobile.notifications.cancelAllFutureLocalNotifications();

        if (redirect) {
            localization.initialize(function (locale) {
                $f.application.content.show(new LoginForm({ active_locale: locale }));
            });
        }

        if (!$f.active_user.is_logged_in()) {
            return;
        }

        if (isWeb()) {
            unregisterDeviceForPWA();
        } else {
            if ($f.flags.get_flag('push_registration_refactor')) {
                unregisterDevice();
            } else {
                $f.mobile.push_notifications.unregister_device();
            }
        }

        $f.mobile.settings.clear();
        $f.mobile.remove_active_collection();

        $f.mobile.form_templates.clear_all();
        $f.mobile.form_drafts.clear_all();

        $f.mobile.cache.clear('dashboard_search_options'); // @deprecated @jira:MOBILE-555 - from old dashboard
        $f.mobile.cache.clear_by_substring('accounts'); // can't do the same as above because we use a variety of cache_keys, such as 'accounts' and 'accounts_for_tag_x'
        $f.mobile.cache.clear_by_substring('mobile_dashboard__');
        $f.mobile.cache.clear_by_substring('mobile_tasks__');
        $f.mobile.cache.clear_by_substring('company_distribute_groups');

        $f.mobile.cache.js_unload_all();

        var _clear_user = function () {
            if ($f.active_user) {
                $f.active_user.clear();
                $f.mobile.cache.clear('active_user');
            }

            if ($f.active_company) {
                $f.active_company.clear();
                $f.mobile.cache.clear('active_company');
            }
        };

        window.zE('messenger', 'logoutUser');

        var logout_ajax = function () {
            $f.mobile.ajax({
                url: '/logout_ajax/',
                type: 'GET',
                cache: false,
                success: function (response) {
                    // @author:david we need to wait until we've actually logged ourselves out from the server to clear
                    // our local $f.active_user, etc.
                    _clear_user();
                },
                error: function (response) {
                    _clear_user();
                    $f.mobile.logging.log_error('Unable to call proper logout.');
                }
            });
        };

        // put this on a delay in case anything else needs to hit the server
        // while logged in.  at this point, the login form should be shown anyway
        // so there is no need to wait
        setTimeout(logout_ajax, 1500);

        return false;
    },

    set_viewport: function () {
        // we need to wait a little for the rotation to finish before we measure. annoying, yes.
        $f.window.viewport = { width: $(window).width(), height: $(window).height() };
        setTimeout(function () {
            $f.window.viewport = { width: $(window).width(), height: $(window).height() };
            $f.application.vent.trigger('app:orientation_change');
        }, this.DELAY_ON_ORIENTATION_CHANGE_MS);
    },

    /**
     * Set whether or not the LeftMenu should be accessible by swiping.
     */
    set_leftnav_swipe_enabled: function (is_enabled) {
        is_enabled = is_enabled !== false;

        if (is_enabled) {
            $('#leftmenu_dragger').removeAttr('data-snap-ignore');
        } else {
            $('#leftmenu_dragger').attr('data-snap-ignore', 'true');
        }
    },

    /**
     * Update the height of the .page_scrollable element programmatically. Eventually, we can replace this with
     * flexbox positioning.
     */
    update_page_scrollable_height: function ($container_el) {
        const $page_scrollable = $container_el.find('.page_scrollable');
        const body_height = $('body').height();

        if ($page_scrollable.length > 1) {
            $f.mobile.logging.log_error('Multiple .page_scrollable elements found in on_orientation_change()', {
                view: this.template
            });
        }

        if (!$page_scrollable.length) {
            return;
        }

        // @author:david unfortunately, on iOS we need to push the selected input out of the way so it's not trapped
        // behind the keyboard. We don't do this for Android, since the OS does this for us.
        if (isIos() && isNative() && $container_el.find(':focus').length) {
            // if we're focused, just add some padding
            $page_scrollable.addClass('keyboard_open');

            // Don't bother moving the input if it's not behind the keyboard
            if (body_height - $container_el.find(':focus').offset().top > $f.mobile.keyboard_height) {
                return;
            }

            // this.scroll_to_element($container_el.find(':focus'), 300);
            $f.mobile.scroll_to_element($page_scrollable, $container_el.find(':focus'), 300);
            return;
        }

        $page_scrollable.removeClass('keyboard_open');

        var page_scrollable_top = $page_scrollable.offset().top;
        var new_height = body_height - page_scrollable_top;

        // consider any elements that are taking up footer room
        $container_el.find('.page_scrollable_height_ignore').each(function (i, el) {
            var $el = $(el);

            // If this element is above the .page_scrollable area, don't consider it
            if ($el.offset().top < $page_scrollable.offset().top) {
                $f.mobile.logging.log_error('Found .page_scrollable_height_ignore above .page_scrollable. Ignoring.', {
                    el_classname: $el.attr('class')
                });

                return true; // continue
            }

            new_height -= $el.outerHeight();
        });

        $page_scrollable.css('height', new_height);
    },

    /**
     * Scroll to an element or a distance from top.
     *
     * @param  {element}        $page_scrollable    The scrollable element that contains the target
     * @param  {element|number} target              The element to scroll to. This could also be a number for distance from top
     * @param  {number}         speed               How fast should we scroll?
     * @param  {number}         offset              How much should offset should we scroll to?
     */
    scroll_to_element: function ($page_scrollable, target, speed, offset) {
        if (typeof speed === 'undefined') {
            speed = 400;
        }
        if (typeof offset === 'undefined') {
            offset = -100;
        }

        if (isNaN(target) && !target.length) {
            console.warn('Warning: Could not find element to scroll to:', target);
            return;
        }

        var destination = null;

        if (isNaN(target)) {
            destination = target.offset().top - $page_scrollable.offset().top + $page_scrollable.scrollTop() + offset;
        } else {
            destination = target + offset;
        }

        // We should be using Velocity for this, but it doesn't always work and is buggy
        $page_scrollable.animate(
            {
                scrollTop: destination
            },
            speed
        );
    },

    /**
     * When the mobile app is resumed, this event will fire and any view that is listening
     * can handle this situation
     */
    resume: function () {
        if (
            $f.application.content.currentView &&
            $f.application.content.currentView.r_main_content &&
            $f.application.content.currentView.r_main_content.currentView &&
            $f.application.content.currentView.r_main_content.currentView.on_resume
        ) {
            $f.application.content.currentView.r_main_content.currentView.on_resume();
        }
    },

    /**
     * Our own Ajax implementation to handle standardized error callbacks, etc. This
     * will also prefix our URLs with API_URL.
     *
     * @param {function} on_upload_progress Our own upload progress callback wrapper, which passes a percentage back
     */
    ajax: function (options) {
        var error_cb = options.error || function () {};

        if (!isConnected()) {
            error_cb({ message: 'You are currently offline' });
            return;
        }

        options.timeout = !options.timeout ? 30000 : options.timeout;
        options.url = API_URL + options.url;

        if (options.url.includes('/api/v1/tasks/list_tasks/')) {
            // To improve performance on mobile, we prevent the backend from querying to calculate `has_comments`.
            options.data = options.data || {};
            options.data.check_has_comments = false;
        }

        options.xhrFields = options.xhrFields || {};
        options.xhrFields.withCredentials = true; // needed for CORS

        /**
         * Handle upload progress for native vs web (if requested)
         */
        if (isWeb() && options.on_upload_progress) {
            var old_xhr = options.xhr
                ? options.xhr()
                : function () {
                      return $.ajaxSettings.xhr();
                  };

            options.xhr = function () {
                var xhr = old_xhr();
                if (!xhr.upload) {
                    return xhr;
                }
                if (xhr.upload.onprogress) {
                    return xhr;
                } // don't overwrite the listener if one has already been set

                xhr.upload.onprogress = function (event) {
                    if (!event.lengthComputable) {
                        return;
                    }
                    options.on_upload_progress((event.loaded / event.total) * 100);
                };
                return xhr;
            };
        } else if (isNative() && options.on_upload_progress && !options.progress) {
            options.progress = function (prog) {
                if (!prog.total || !prog.done) {
                    return;
                }
                options.on_upload_progress((prog.done / prog.total) * 100);
            };
        }

        options.error = function (response) {
            // try to parse the error response. If cannot, just return response
            try {
                response = JSON.parse(response.responseText);
            } catch (e) {
                return error_cb(response);
            }

            var should_show_status =
                response &&
                response.status === 403 &&
                $f.mobile.is_initialized &&
                response.title &&
                response.message &&
                response.block_client_usage;

            if (should_show_status) {
                $f.mobile.nav.show(StatusPage, {
                    messaging: {
                        title: response.title,
                        message: response.message
                    }
                });
                return;
            }

            if (
                typeof response.is_authenticated !== 'undefined' &&
                !response.is_authenticated &&
                $f.mobile.is_initialized
            ) {
                $f.mobile.logout();
                return;
            }

            if (!response.success) {
                error_cb(response);
                return;
            }

            error_cb(response);
        };
        options.beforeSend = function (xhr) {
            xhr.setRequestHeader('x-zenput-mobile', $f.mobile.get_version_string());
        };

        $.ajax(options);
    },

    /**
     * Opens given URI. In browsers by default this opens a new tab.
     *
     * @param uri {string} The uri to open (webpage address, store URI, etc)
     * @param target {string} The window/content which the uri will be opened in
     */
    open_uri: function (uri, target, success, error) {
        target = target || '';
        success = success || function () {};
        error = error || function () {};

        // @note @author: daniel if it's running on Android or iOS open the link using the system browser. A work around SSL issues over HTTPS links, see ZP-3013
        // @todo: Once SSL certificate errors are fixed for *.storage.zenput.com, we can remove this special case scenario
        if (isNative() && !isWindows()) {
            CordovaPlugins().InAppBrowser.open(uri, '_system');
            return success();
        }

        if (isWeb() || !isWindows()) {
            window.open(uri, '_self', `target=${target}`);
            return success();
        }

        Windows.System.Launcher.launchUriAsync(new Windows.Foundation.Uri(uri)).then(function (launchUriSucceeded) {
            if (launchUriSucceeded) {
                success();
            } else {
                error();
            }
        });
    },

    /**
     * Utility function for getting the active main view (r_main_content) or overlay view.
     * This is used for DEBUGGING only!
     */
    get_window_view: function () {
        try {
            if ($f.application.content_overlay.currentView && !$f.application.content_overlay.currentView.isClosed) {
                return $f.application.content_overlay.currentView;
            }

            return $f.application.content.currentView.r_main_content.currentView;
        } catch (e) {
            return null;
        }
    },

    /**
     * This is used almost exclusively for mobile web.  We have some funny issues regarding uploads,
     * so we need access to that object
     */
    get_window_collection: function () {
        try {
            return $f.application.content.currentView.r_main_content.currentView.collection;
        } catch (e) {
            return null;
        }
    },

    /**
     * Retrieve the active collection from local storage.
     * @param  {Function} callback The callback with the collection, if it exists
     */
    get_active_collection_from_local: function (callback) {
        callback = callback || function () {};

        $f.mobile.storage.get('active_collection', function (collection) {
            if (collection) {
                collection = new MobileFormStructure(collection);
            }

            callback(collection);
        }); // this should never happen
    },

    save_active_collection: function (collection, callback) {
        callback || (callback = function () {});

        // Don't save if this collection was already submitted. We never want an active collection
        // to be a previous submission, as it could get resubmitted.
        if (collection.has_been_submitted()) {
            return callback();
        }

        // To prevent it from being lost, lock the drawer to prevent the user from getting out
        $f.mobile.set_leftnav_swipe_enabled(false);

        // theres a serious bug in Trigger where prefs.set is causing $f.window.active_collection
        // to be overwritten with an older version.
        $f.mobile.storage.set('active_collection', collection, function () {
            callback();
        });
    },

    remove_active_collection: function (callback) {
        callback = callback || function () {};

        // no active collection to protect, open the drawer
        $f.mobile.set_leftnav_swipe_enabled(true);

        $f.mobile.storage.set('active_collection', null, callback);
    },

    /**
     * Get a form collection by ID from local or the server.
     */
    get_form_collection: function (rdbms_id, task_id, success, error) {
        success = success || function () {};
        error = error || function () {};

        var try_online = function (local_collection) {
            $f.mobile.form_drafts.get_task_draft(
                rdbms_id,
                task_id,
                function (value) {
                    success(value);
                },
                function () {
                    // no draft found
                    $f.mobile.form_templates.get_record(
                        rdbms_id,
                        null,
                        function (value) {
                            success(value);
                        },
                        function (response) {
                            // error recovery - lets see if we can pull from disk.
                            if (!response && local_collection) {
                                // hmm, maybe they were offline.  either way, we have a collection, render it
                                success(local_collection);
                                return;
                            }

                            // no recovery.  lets just give them an error
                            $f.mobile.remove_active_collection(function () {
                                error();
                                showErrorMessage();
                            });
                        },
                        isConnected()
                    );
                }
            );
        };

        // first grab the version from disk, then hit the server to see if there is a newer one
        // dont pass in an error callback, and make sure force == false, i.e. hit the disk
        $f.mobile.form_templates.get_record(rdbms_id, null, try_online, try_online, false);
    },

    /**
     * Check if the template is available locally, and if its not out dated.
     * Otherwise, check the server
     *
     * @param  {int}                        rdbms_id      The Template RDBMS ID of the form you want to load
     * @param  {boolean}                    animate       If true, the collections view will transition into place
     */
    load_collection: function (rdbms_id, task_id, animate, success) {
        animate || (animate = false);
        success || (success = function () {});

        showLoadingDialog(); // show_collection below will hide our loading dialog for us.
        // (we shouldn't call explicitly, because the callback occurs after
        // the mobile form has been transitioned in)

        var _err = function () {
            hideLoadingDialog();
            $f.application.content.show(new MobileAppLayout()); // can't find it, so just render the app layout
        };

        this.get_form_collection(
            rdbms_id,
            task_id,
            function (collection) {
                if (!collection) {
                    $f.mobile.logging.log_error('Unable to fetch template. Error callback should have been called.', {
                        rdbms_id: rdbms_id,
                        task_id: task_id
                    });

                    return _err();
                }

                $f.mobile.show_collection(collection, { animate: animate }, function () {
                    success(collection);

                    // smetadata is not one of the collection's models, so whatever `success`
                    // attached to it raises no 'change'. Without an explicit write a form
                    // restored before its first answer comes back bare.
                    $f.mobile.save_active_collection(collection);
                });
            },
            _err
        );
    },

    /**
     * Load a collection onto the main screen
     *
     * @param  {Backbone.Collection}    collection                      The collection (Template) to show
     * @param  {dict}                   options                         Additional options as follows:
     * @param  {boolean}                options.animate                 True if should animate into view, false if should not animate into view
     * @param  {string}                 options.transition_direction    The transition direction (transition_from) that should be applied to the animation (if applicable)
     * @param  {function}               callback                        A callback to be fired after the collection is shown
     */
    show_collection: function (collection, options, callback) {
        options = options || {};
        callback || (callback = function () {});
        if (!options.animate) {
            options.animate = false;
        }

        showLoadingDialog();

        // check to see if the MobileFormLayout exists, if so, redraw the main portion,  if not, redraw from scrach
        $f.mobile.save_active_collection(collection, function () {
            hideLoadingDialog();

            if ($f.application.content.currentView && $f.application.content.currentView.r_main_content) {
                var form_identifier = collection.smetadata.get('date_submitted') ? SubmittedFormPage : MobileFormPage;
                $.extend(options, { collection: collection, use_transitions: options.animate });
                $f.mobile.nav.show(form_identifier, options);
                callback();
                return;
            }

            // lets draw a whole new page
            $f.application.content.show(new MobileAppLayout({ collection: collection }));
            callback();
        });
    },

    /**
     * Initialize our main MobileAppLayout view. First, look for an active collection
     * in local storage for use by view, if available.
     */
    init_app_layout: function (initial_page_data, callback) {
        initial_page_data = initial_page_data || {};
        callback = callback || function () {};

        hideLoadingDialog();

        var _init = function () {
            localization.initialize(function (locale) {
                $f.utils.remove_url_hashbangs();

                // check to see if there's an active_collection we should show
                $f.mobile.get_active_collection_from_local(function (collection) {
                    if (collection && !initial_page_data.klass) {
                        initial_page_data = {
                            klass: MobileFormPage,
                            collection: collection
                        };
                    }

                    $f.application.content.show(
                        new MobileAppLayout({
                            initial_page_data: initial_page_data
                        })
                    );

                    if (collection) {
                        $f.mobile.set_leftnav_swipe_enabled(false); // must be set after showing MobileAppLayout above
                    }
                    callback();
                });
            });
        };

        $f.mobile.is_oia_dialog_overdue(function (is_overdue) {
            if (!is_overdue) {
                return _init();
            }
            $f.mobile.nav.show(OiaDialog, {
                on_dismiss_for_web: function () {
                    _init();
                }
            });
        });
    },

    /**
     * So that get_device_location() can be called multiple times back-to-back,
     * we will keep a queue of requests to call in order after previous requests finish.
     */
    _geolocation_request_queue: [],
    geolocation_in_progress: false,

    /**
     * Get the device's location using HTML5's or Phonegap's getCurrentPosition(). This wrapper
     * accounts for differences in API for iOS vs Android and web vs native.
     *
     * For more information see Phonegap Documentation:
     * https://github.com/apache/cordova-plugin-geolocation
     *
     * Or for HTML5:
     * http://www.w3schools.com/html/html5_geolocation.asp
     *
     * @param {function} options                            Parameters and settings for getting the device location, such as callbacks, timeouts, etc.
     * @param {function} options.success                    The success callback, passed with a position object
     * @param {function} options.error                      The error callback, passed with an error with a code matching one of the following:
     *                                                          error.PERMISSION_DENIED - the user denied access to their position
     *                                                          error.POSITION_UNAVAILABLE - the location could not be determined
     *                                                          error.TIMEOUT - the location request timed out
     *
     * @param {function} options.complete                   Function to fire after both success and fail
     * @param {int}       options.timeout                   The amount of time in milliseconds that should be allowed to retrieve location (Defaults to 7000).
     * @param {int|null} options.cache_allowance_ms         Timestamp in milliseconds; will use cached location instead of new gps request if it has been less
     *                                                      than this amount of time since cached
     * @param {int|null} options.force_request_permission   iOS/Web Only: if the user hasn't given permission, force a message that tells them. Otherwise, we will
     *                                                      only prompt them every 3 days
     * @param {int|null} options.only_if_permission_granted iOS/Web Only: do not get geolocation if user has not previously given us permission
     * @param {boolean}  options.fallback_to_cache          If true, will use the cache if it is available upon failing to fetch live geolocation from GPS
     */
    get_device_location: function (options) {
        var this_ = this;

        /**
         * Androdi 6.0+: when the permission dialog opens, the app is 'paused' and Javascript continues running.
         * If get_device_location() is called again before the user has accepted/denied, we need to queue up that
         * call for when the app is resumed again. Note that Android is the only platform that CAN ask multiple
         * times after the user denies; web and iOS will make the user change the setting themselves.
         */
        if ($f.mobile.geolocation_in_progress) {
            options.only_if_permission_granted = true; // don't ask again if we're denied the first time
            this._geolocation_request_queue.push(options);
            return;
        }

        $f.mobile.geolocation_in_progress = true;

        /**
         * Note: we save a 'geo_data' var in $f.mobile.settings with the following data:
         *
         * 'geo_data.last_known_location' is a cache of the device' s last known location. This is used
         * a) if the user requests a recently cached location or b) if requesting a live location fails.
         *
         * 'geo_data.permissions' is a dict of { has_permission, last_notified }. This is only needed for iOS and web.
         * If the user has granted permission for us to use their location, has_permission will be true.
         * If not, we will notifiy the user every 3 days, and last_notified will be updated with the last
         * notification for it.
         */

        options = options || {};
        options = $.extend(
            {
                // Defaults:
                success: function () {},
                error: function () {},
                complete: function () {},
                timeout: 7000,
                cache_allowance_ms: 180000, // 3 min
                force_request_permission: false, // by default, do not re-ask for permission (iOS, Web)
                only_if_permission_granted: false,
                fallback_to_cache: true
            },
            options
        );

        // HTML5 standard error codes
        var error_codes = {
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
        };

        // after we've completed, call any waiting requests for geolocation
        var _complete = options.complete;
        options.complete = function () {
            $f.mobile.geolocation_in_progress = false;
            _complete.apply(this, arguments);
        };

        // Always complete() after success() and error()
        var _success = options.success;
        options.success = function () {
            _success.apply(this, arguments);
            options.complete();
            if (this_._geolocation_request_queue.length === 0) {
                return;
            }
            const nextOptions = this_._geolocation_request_queue.pop();
            this_.get_device_location(nextOptions);
        };

        var _error = options.error;
        options.error = function (error) {
            _error.apply(this, arguments);
            options.complete();

            while (this_._geolocation_request_queue.length !== 0) {
                const current_request = this_._geolocation_request_queue.pop();
                current_request.error && current_request.error(error);
                current_request.complete && current_request.complete(error);
            }
        };

        // If the device is on web and doesn't have HTML5 support, fail with a pseudo HTML5 error
        if (isWeb() && !navigator.geolocation) {
            options.error($.extend({ code: error_codes.POSITION_UNAVAILABLE }, error_codes));
            return;
        }

        var _geo_timeout = 0;

        $f.mobile.settings.get('geo_data', function (geo_data) {
            geo_data = geo_data || {
                last_known_location: {
                    _cache_timestamp: null,
                    coords: { lat: null, lon: null }
                },
                permissions: null // { has_permission, last_notified }
            };
            // Any changes to geo_data will be saved when we success() or error()
            // (We must do this in success() and error() separately instead of complete()
            // because we want this to run BEFORE success() and error() run.)
            var _get_geo_data_success = options.success;
            var _get_geo_data_success_error = options.error;
            options.success = function () {
                var args_ = arguments;
                var this__ = this;
                $f.mobile.settings.set('geo_data', geo_data, function () {
                    _get_geo_data_success.apply(this__, args_);
                });
            };
            options.error = function () {
                var args_ = arguments;
                var this__ = this;
                $f.mobile.settings.set('geo_data', geo_data, function () {
                    _get_geo_data_success_error.apply(this__, args_);
                });
            };

            var _on_error_was_called = false;
            var _on_error = function (err) {
                // don't let this be called twice, in case both the timeout and the geolocation API call it
                if (_on_error_was_called) {
                    return;
                }
                _on_error_was_called = true;

                clearTimeout(_geo_timeout);

                // Give the user a cached position if possible
                if (options.fallback_to_cache && Boolean(geo_data.last_known_location.coords.lat)) {
                    geo_data.last_known_location._is_cached = true;
                    options.success(geo_data.last_known_location);
                    return;
                }

                /**
                 * Unfortunately, sometimes the error code is incorrect (Android 6+ gives POSITION_UNAVAILABLE and iOS
                 * gives undefined). We can tell if permission was denied from the error message, we can't mutate
                 * err.code. Thus, we will create a new error with the correct code if necessary.
                 */
                var get_patched_error = function (_err) {
                    if (
                        _err.code === error_codes.PERMISSION_DENIED ||
                        _err.message === 'Permission denied' ||
                        _err.message === 'application does not have sufficient geolocation permissions.' ||
                        (_err.message && _err.message.indexOf('Access is denied.') !== -1) // windows
                    ) {
                        var new_err = new Error(_err.message);
                        new_err.code = error_codes.PERMISSION_DENIED;
                        return new_err;
                    }

                    return _err;
                };

                err = get_patched_error(err);

                // If we don't have permission and we haven't yelled at them recently, tell the user to give us geo permissions (iOS, Android 6+, and Web)
                if (err.code === error_codes.PERMISSION_DENIED) {
                    if (
                        !geo_data.permissions ||
                        options.force_request_permission ||
                        (new Date().getTime() - geo_data.permissions.last_notified > 259200000 && // less than 3 days
                            !geo_data.permissions.has_permission)
                    ) {
                        // @TODO We are not asking users to enable location if they've already denied it.
                        // Uncomment the following if that changes. Also, use a wrapper alert() instead of web alert():
                        //
                        // showPermissionDeniedMessage('location');

                        geo_data.permissions = { has_permission: false, last_notified: new Date().getTime() };
                    }
                }

                options.error(err);
            };

            // If the user asked for a cached position and it qualifies, try to just return that
            if (options.cache_allowance_ms !== 0 && Boolean(geo_data.last_known_location.coords.lat)) {
                if (new Date().getTime() - geo_data.last_known_location._cache_timestamp < options.cache_allowance_ms) {
                    // make sure it's not too old
                    options.success(geo_data.last_known_location);
                    return;
                }
            }

            // (iOS/Web) If we don't already have permission to access location and we're not supposed to ask, return an error
            if (
                (!geo_data.permissions || !geo_data.permissions.has_permission) && // we don't have permission
                options.only_if_permission_granted // we aren't supposed to ask
            ) {
                options.error($.extend({ code: error_codes.PERMISSION_DENIED }, error_codes));
                return;
            }

            // Sometimes Firefox does not time out time out as it should, so this is our failsafe.
            // @author:david Don't do this on Android because if this calls _on_error() before the permission
            // dialog disappears, options.error() will try to start the next fetch before we're ready.
            if (!(isAndroid() && isNative())) {
                _geo_timeout = setTimeout(function () {
                    _on_error($.extend({ code: error_codes.TIMEOUT }, error_codes));
                }, options.timeout + 100);
            }

            // Ok, finally get live location!
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    clearTimeout(_geo_timeout);
                    // Double check that we have a lat and long (this should never happen)
                    if (!position.coords.latitude || !position.coords.longitude) {
                        _on_error($.extend({ code: error_codes.POSITION_UNAVAILABLE }, error_codes));
                        return;
                    }

                    geo_data.last_known_location._cache_timestamp = new Date().getTime();
                    geo_data.last_known_location.coords = {
                        // Firefox doesn't like to store the whole position object, so just save explicitly what we need
                        lat: position.coords.latitude,
                        lon: position.coords.longitude
                    };
                    geo_data.permissions = { has_permission: true, last_notified: 0 };

                    options.success(geo_data.last_known_location);
                },
                function (err) {
                    _on_error(err);
                },
                { timeout: options.timeout }
            );
        });
    },

    load_sentry: function () {
        const sentryDsn = isReleaseBuild() ? process.env.SENTRY_DSN_RELEASE : process.env.SENTRY_DSN_DEBUG;

        if (!sentryDsn) {
            return;
        }

        const sentry = $f.mobile.logging.get_sentry();

        sentry.init({
            dsn: sentryDsn,
            logger: isNative() ? 'mobile_cordova' : 'mobile_web'
        });
    },

    /**
     * Loads up the Google Maps API asynchronously. If already loaded, this will not reload. If offline,
     * this will call the error() callback, UNLESS the API has already been loaded.
     *
     * @param {object}   options                            Options for the request:
     * @param {function} options.success                    The success callback, passed with a position object
     * @param {function} options.error                      The error callback, passed with an error with a code matching one of the following:
     */
    load_google_maps_api: function (options) {
        options = options || {};
        options = $.extend(
            {
                // Defaults:
                success: function () {},
                error: function () {}
            },
            options
        );

        // Check if we already loaded Maps.  If so, call success
        if (typeof google != 'undefined' && google.maps) {
            options.success();
            return;
        }

        // just make sure we're online
        if (!isConnected()) {
            options.error();
            return;
        }

        var failsafe_timeout = null;

        // Create global callback function that Google Maps API will call when it's loaded
        var function_name = '_on_maps_api_loaded_' + new Date().getTime() + Math.floor(Math.random() * 1000 + 1); // pseudo-guid
        $f.mobile[function_name] = function () {
            options.success();
            delete $f.mobile[function_name];
            clearTimeout(failsafe_timeout);
        };

        // Failsafe callback in case something goes wrong
        failsafe_timeout = setTimeout(function () {
            // If our callback is uncalled after 7 seconds, bail and call error()
            if ($f.mobile[function_name]) {
                $f.mobile[function_name] = function () {}; // don't completely delete this in case the request completes and tries to call it
                options.error();
            }
        }, 7000);

        // Make the call!
        $.ajax({
            url: `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${process.env.GOOGLE_MAPS_KEY_JS}&callback=$f.mobile.${function_name}`,
            dataType: 'script',
            error: function () {
                options.error();
            }
        });
    },

    /**
     * Determine if it's time to show the OIA dialog. We should show it if we've
     * never shown it before or we last showed it more than 4 days ago.
     *
     * @param {function} answer_callback The function to call with the answer (true or false) passed in
     */
    is_oia_dialog_overdue: function (answer_callback) {
        const isSupportedPlatform = isIos() || isAndroid();

        if (isNative() || !isSupportedPlatform || window.IS_FORM_BUILDER_PREVIEW) {
            answer_callback(false);
            return;
        }

        $f.mobile.settings.get('oia', function (last_oia_shown) {
            if (last_oia_shown && new Date().getTime() < parseInt(last_oia_shown) + 345600000) {
                // less than 4 days ago
                answer_callback(false);
                return;
            }

            answer_callback(true);
        });
    },

    redirect: function (url) {
        url = url || '';

        if (url.indexOf('zenput://saml2/') != -1) {
            // Pass the user JWT through the mobile client to the back-end.
            Djangosaml2ClientAuth.onRedirectUri(getSamlJwtFromDeepLink(url));
            return;
        }

        url = url.replace(/\/$/, '');

        var match = url.match('zenput://(.*)?');
        if (!match || (window.location.hash && window.location.hash != '#noop')) {
            return;
        } // if the match doesn't match, or we've already done the redirect/reload, we're good to go

        var new_hash = match[1];
        if (!new_hash) {
            return;
        } // just zenput:// will open the app, no redirect

        var _redirect = function () {
            // If the app was just launched from scratch (i.e., not coming from the background), we need to do a full reload with the new #hash
            // in order to prevent the default route (already in progress at this point) from interfering.
            if (!$f.window.has_resumed) {
                window.location.href = `?uniqueload=${new Date().getTime()}#${new_hash}`; // using ? param will cause a full redirect in one instant step
                return;
            }

            // Otherwise, if the app is being resumed and is already running, there is no default route being executed, so we don't need to do a full refresh. We
            // could, but there can be a noticable flicker as the page reloads (Android). Instead, we'll just redirect to the #hash.

            if (window.location.hash == '#noop') {
                // Something went wrong earlier and we're still on #noop. Just go straight to #hash
                $f.window.app_router.navigate(new_hash, true);
                return;
            }

            $(window).on('hashchange.zp_redirector', function (e) {
                // wait for #noop to apply
                $(window).off('hashchange.zp_redirector'); // stop listening now that we got it
                $f.window.app_router.navigate(new_hash, true);
            });

            window.location.hash = 'noop';
        };

        // check to see if there's an active_collection we might want to save as a draft first
        $f.mobile.get_active_collection_from_local(function (collection) {
            if (!collection || !collection.is_started()) {
                return _redirect();
            }

            confirmNotification(
                _t('mobile_message_redirect_confirm_message'),
                function (success) {
                    if (!success) {
                        $f.mobile.remove_active_collection(function () {
                            _redirect();
                        });

                        return;
                    }

                    $f.mobile.form_drafts.add_record(collection, function () {
                        setTimeout(function () {
                            _redirect();
                        }, 0);
                    });
                },
                _t('mobile_common_button_save_draft'),
                _t('mobile_common_button_yes'),
                _t('mobile_common_button_no')
            );
        });
    },

    get_state_icon_for_task: function (state, is_completed_late) {
        // if state is 'complete_with_exception', we should be using an SVG.
        // Return 'unavailable' just in case it gets displayed
        if (!state) {
            return 'df-unavailable';
        }

        if (state === 'complete_with_exception') {
            if (is_completed_late) {
                return 'status-completed-with-exception df_status_completed_late';
            }
            return 'status-completed-with-exception';
        } else if (state === 'complete') {
            if (is_completed_late) {
                return 'df-status-completed df_status_completed_late';
            }
            return 'df-status-completed';
        } else if (state === 'processing') {
            return 'df-status-completed status-processing';
        } else if (state === 'unavailable') {
            return 'df-unavailable';
        } else if (state === 'overdue') {
            return 'df-status-overdue';
        } else if (state === 'incomplete_and_closed') {
            return 'df-status-overdue-and-closed';
        } else if (state === 'open') {
            return 'df-status-open';
        }

        $f.mobile.logging.log_error(
            'Mobile Dashboard: unknown current_state of',
            state,
            '- defaulting to unavailable.'
        );
        return 'df-unavailable';
    },

    /**
    This function is used to show overflow_touchblock when a menu such as overflow menu or typeahead
    or didn't create the form
    * @param $touch_block {jQuery}
    * @param callback {Function}
    */
    show_overflow_touchblock: function ($touch_block, callback) {
        callback = callback || function () {};
        this.$touch_block = $touch_block;

        if (!this.$touch_block) {
            throw new Error('ProgrammerError: $touch_block is required for $f.mobile.show_overflow_touchblock');
        }

        $touch_block.show();

        // Rather than using Backbone events, let's user Hammer tap/swipe
        this._hammertime = new Hammer($touch_block.get(0), {});
        this._hammertime.get('pan').set({ direction: Hammer.DIRECTION_ALL }); // allow all pan directions

        this._hammertime.on('tap pan', function (event) {
            event.preventDefault();
            callback();
        });
    },

    /**
    This function is used to hide overflow_touchblock when a menu such as overflowm menu or typeahead
    or didn't create the form
    * @param callback {Function}
    */
    hide_overflow_touchblock: function (callback) {
        callback = callback || function () {};

        $('.overflow_touchblock').hide();
        if (this._hammertime) {
            this._hammertime.off('tap pan', callback);
        }
    },

    /**
     * Returns a string with the current app version
     * @returns {string}
     */
    get_version_string: function () {
        if (isWeb()) {
            return 'Web';
        }

        return $f.mobile.settings.get_release_details().release_native;
    }
}; /* end $f.mobile */

/**
 * This calculates and keeps track of upload progress and manages pending uploads.
 *
 * @see also showUploadProgress()
 */
mobile.uploads = {
    web_filestore: {}, // @see $f.mobile.uploads.web_filestore

    upload_states: {
        PENDING: 1,
        ERROR: 2,
        SUCCESS: 3,
        ACTIVE: 4,
        MISSING_FILE: 5
    },

    errors: {
        CONNECTION: 1
    },

    PENDING_UPLOADS_CACHE_KEY: 'pending_uploads',
    UPLOADER_MAX_RETRIES: 3,
    AUTHENTICATION_TTL: 84600000, // 23.5 hours (should be 24, but add a little room to account for time drift)

    _max_concurrent_file_uploads: 3,
    _max_concurrent_file_uploads_windows: 1,
    _is_authenticating: false,
    _pending_uploads: {},
    _upload_listeners: {},
    _upload_error_count: 0,
    _active_upload_success_msg: null,
    _active_upload_error_msg: null,

    /**
     * How many file uploads should we perform simultaneously?
     */
    get_max_concurrent_uploads: function () {
        if (isNative() && isWindows()) {
            // windows still uploads via zenput.com, so we shouldn't clog the servers @jira:MOBILE-694
            return this._max_concurrent_file_uploads_windows;
        }

        return this._max_concurrent_file_uploads;
    },

    /**
     * Get the number of SUBMISSION (not image/file) uploads.
     *
     * @param exclude_drafts {boolean} - should we exclude drafts from the total count
     * @return {int} The number of submissino uploads pending
     */
    get_num_pending_submission_uploads: function (exclude_drafts) {
        var count = 0;
        exclude_drafts = exclude_drafts || false;

        this.for_each_pending_upload_structure(function (upload_collection) {
            // don't show form drafts in the count in the left nav.
            if (exclude_drafts && upload_collection.keep_records) {
                return;
            }
            count += upload_collection.get_indexes_for_upload().length;
        });

        return count;
    },

    /**
     * Set a model's ID to a unique GUID and a flag of local_guid=true. We do this for all task-related
     * uploads (FastTasks, NewTasks, Projects), and it's used by the upload code to keep track of which file uploads
     * belong to which structural uploads.
     */
    set_model_guid: function (model) {
        if (model.get('local_guid') && model.id) {
            return; // already set
        }

        model.set({
            id: model.id || $f.utils.local_guid(), // if it's already been set, don't want to change it
            local_guid: true
        });
    },

    /**
     * Notify pending form on upload failure. If we're failing too often
     * pause the uploader until the user attempts another upload.
     */
    on_upload_error: function (pending_upload, error) {
        this._upload_error_count++;
        this.upload_pending_files();
    },

    /**
     * Notify pending form on upload success.
     */
    on_upload_success: function (pending_upload) {
        var listener = this._upload_listeners[pending_upload.get('parent_upload_guid')];

        if (listener) {
            listener.success(pending_upload);
        }

        this._on_pending_uploads_modified();
        this.upload_pending_files(true); // see if there's anything else we should upload
    },

    /**
     * Persist pending uploads to disk.
     */
    save_pending_uploads: function () {
        $f.mobile.storage.set($f.mobile.uploads.PENDING_UPLOADS_CACHE_KEY, JSON.stringify(this._pending_uploads));
    },

    /**
     * Load pending uploads from cache.
     */
    load_pending_uploads: function (callback) {
        var this_ = this;

        $f.mobile.storage.get($f.mobile.uploads.PENDING_UPLOADS_CACHE_KEY, function (pending_uploads) {
            if (!pending_uploads) {
                return callback();
            }

            _.each(JSON.parse(pending_uploads), function (val, key) {
                var pending_upload = new UploadableFile(val);

                if (pending_upload.is_upload_active()) {
                    pending_upload.set('status', $f.mobile.uploads.upload_states.PENDING);
                }

                this_._pending_uploads[key] = pending_upload;
            });

            callback();
        });
    },

    set_upload_listener: function (parent_upload_guid, success, error) {
        success = success || function () {};
        error = error || function () {};

        this._upload_listeners[parent_upload_guid] = { success: success, error: error };
    },

    remove_upload_listener: function (parent_upload_guid) {
        if (this._upload_listeners[parent_upload_guid]) {
            delete this._upload_listeners[parent_upload_guid];
        }
    },

    /**
     *  Remove all pending uploads associated with the given parent_upload_guid.
     *  The main use case is cleaning up uploads after a pending submission finishes.
     */
    clear_pending_file_uploads: function (parent_upload_guid) {
        if (!parent_upload_guid) {
            return;
        }

        // todo:bruce user could kill app etc before save completes
        // so we might want to have some other method to clear stale uploads
        // for example, on load we could look at all group ids and clear all uploads that with no
        // cache entry in pending uploads, etc

        // error on clearing group with pending uploads, or let this act as a way to cancel uploads as well?
        var this_ = this;
        _.each(this._pending_uploads, function (pending_upload, key) {
            if (pending_upload.get('parent_upload_guid') !== parent_upload_guid) {
                return true;
            }

            pending_upload.cancel_upload();

            removeLocalFile(pending_upload.get('filename'));
            delete this_._pending_uploads[key];
        });

        this._on_pending_uploads_modified();
    },

    get_pending_upload: function (filename) {
        return this._pending_uploads[filename];
    },

    get_total_num_active_uploads: function () {
        var num_active_uploads = 0;

        _.each(this._pending_uploads, function (pending_upload) {
            if (pending_upload.is_upload_active()) {
                num_active_uploads++;
            }
        });

        return num_active_uploads;
    },

    /**
     *  Return the total number of pending uploads.
     *  Pending and failed uploads are counted.
     */
    get_total_num_pending_uploads: function () {
        var num_pending_uploads = 0;

        _.each(this._pending_uploads, function (pending_upload) {
            if (pending_upload.needs_upload()) {
                num_pending_uploads++;
            }
        });

        return num_pending_uploads;
    },

    /**
     *  Return the number of remaining uploads for the given upload group.
     *  Remaining uploads counts pending, failed, and running upload states.
     */
    get_num_remaining_uploads: function (parent_upload_guid) {
        var num_remaining_uploads = 0;

        _.each(this._pending_uploads, function (upload) {
            if (!upload.is_upload_finished() && upload.get('parent_upload_guid') === parent_upload_guid) {
                num_remaining_uploads++;
            }
        });

        return num_remaining_uploads;
    },

    /**
     *  Pause background uploading. The purpose of this is to shut off uploading
     *  in scenarios where uploads are failing often.
     */
    stop_file_uploads: function () {
        _.each(this._upload_listeners, function (listener, parent_upload_guid) {
            listener.error();
        });

        _.each(this._pending_uploads, function (pending_upload) {
            pending_upload.cancel_upload();
        });

        this._on_pending_uploads_modified();
    },

    /**
     *  Kick off any pending uploads (rate limited by get_max_concurrent_uploads())
     *  If necessary, persist state of pending uploads to cache.
     */
    upload_pending_files: function (reset_error_count) {
        var this_ = this;

        if (reset_error_count) {
            this._upload_error_count = 0;
        }

        if (!Object.keys(this._pending_uploads).length) {
            // nothing to upload
            return;
        }

        // stop polling pending uploads if we're disconnected
        if (!isConnected()) {
            this.stop_file_uploads();
            return;
        }

        if (this._is_authenticating) {
            return; // we're already fetching AWS auth
        }

        if (this._upload_error_count >= $f.mobile.uploads.UPLOADER_MAX_RETRIES) {
            this.stop_file_uploads(); // we've tried enough, let's bail
            return;
        }

        // Better to refresh storage auth once than once per file upload
        $f.storage.refresh({
            success: function () {
                var num_free_pending_uploads =
                    $f.mobile.uploads.get_max_concurrent_uploads() - this_.get_total_num_active_uploads();
                var old_num_free_pending_uploads = num_free_pending_uploads;

                _.each(this_._pending_uploads, function (pending_upload, key) {
                    if (num_free_pending_uploads <= 0) {
                        return false;
                    }

                    if (!pending_upload.needs_upload()) {
                        return true; // continue
                    }

                    pending_upload.upload({
                        auth: this_._s3_auth,
                        success: this_.on_upload_success.bind(this_),
                        error: this_.on_upload_error.bind(this_)
                    });

                    num_free_pending_uploads--;
                });

                // if we kicked off any uploads the pending_uploads cache is invalid and needs to be persisted to disk.
                if (old_num_free_pending_uploads !== num_free_pending_uploads) {
                    this_._on_pending_uploads_modified();
                }
            },
            error: function () {
                this_.on_upload_error(); // this will fail the submission by calling stop_file_uploads()
            }
        });
    },

    /**
     * Enqueue the provided file for background uploading.
     *
     * @param  {Object} options                         Attributes used to constrct a UploadableFile
     * @param  {Object} options.file                    A file object
     * @param  {String} options.file_type               Whether or not the file is a signature
     * @param  {String} options.parent_upload_guid      A guid shared between the form or task and all its file uploads.
     * @param  {String} options.upload_filename         Filename formatted correctly for uploading to the backend
     *
     * WARNING: the file.name here MUST be unique. Otherwise, we could have collisions within this._pending_uploads,
     * and a single file upload could be unexpectedly shared across multiple fields. This shouldn't be a problem
     * because we copy images to our own directory with our own unique name, but it's still fragile. @jira:REPT-482
     */
    add_pending_file_upload: function (options) {
        if (!options.parent_upload_guid) {
            throw new Error('Programmer Error: missing parent_upload_guid in add_pending_file_upload()');
        }

        if (!options.file || this.get_pending_upload(options.file.name)) {
            return;
        }

        var filename = options.file.get('raw_file').name;

        // Web doesn't rename files when editing, reuploading is necessary.
        if (this._pending_uploads[filename]) {
            if (isWeb()) {
                if (options.file.get('url_base64') === this._pending_uploads[filename].get('file').get('url_base64')) {
                    // we've already added it. Don't want to re-upload if we don't have to.
                    return;
                }

                // We generate a different upload/S3 path/filename each time we try to upload an image.
                // To avoid uploading multiple copies of the same image we'll reuse the path
                options.upload_filename = this._pending_uploads[filename].get('upload_filename');
            } else {
                // we've already added it. Don't want to re-upload if we don't have to.
                return;
            }
        }

        this._pending_uploads[filename] = new UploadableFile({
            file: options.file,
            filename: filename,
            file_type: options.file_type,
            upload_filename: options.upload_filename,
            parent_upload_guid: options.parent_upload_guid,
            status: $f.mobile.uploads.upload_states.PENDING
        });

        this._on_pending_uploads_modified();
        this.upload_pending_files(true); // kick uploader for background uploads
    },

    any_remaining_uploads: function (parent_upload_guid) {
        return this.get_num_remaining_uploads(parent_upload_guid) > 0;
    },

    _on_pending_uploads_modified: function () {
        // calling  save_pending_uploads() may mean we should be rate limiting it a bit better
        this.save_pending_uploads();
    },

    /**
     * Get an array of classes used for pending uploads. This can't be a plain constant
     * because not all of these have been loaded at the time of running here.
     */
    get_all_pending_local_records_classes: function () {
        return [
            $f.mobile.pending_form_submissions,
            $f.mobile.pending_task_submissions,
            $f.mobile.form_drafts,
            $f.mobile.new_tasks,
            $f.mobile.new_project,
            $f.mobile.fast_tasks
        ];
    },

    for_each_pending_upload_structure: function (callback) {
        _.each(this.get_all_pending_local_records_classes(), function (pending_upload, i) {
            callback(pending_upload);
        });
    },

    /**
     * Get the upload progress of all pending upload files.
     */
    get_total_files_upload_progress: function () {
        // First, collect all the uplaod guids that are waiting for upload (i.e., not in drafts)
        var uploading_guids = [];
        this.for_each_pending_upload_structure(function (pending_upload_structure) {
            _.each(pending_upload_structure.pluck('id'), function (guid) {
                uploading_guids.push(guid);
            });
        });

        var num_file_uploads = 0;
        var percentage_done = 0;

        // Tally up our upload percentages for any file upload matching the guid's we've tracked
        _.each(this._pending_uploads, function (pending_upload, key) {
            if (!_.contains(uploading_guids, pending_upload.get('parent_upload_guid'))) {
                return true; // continue; this file is not being uploaded now
            }

            num_file_uploads++;
            percentage_done += pending_upload.get('upload_percentage');
        });

        if (!num_file_uploads) {
            return 0; // don't wanna divide by 0!
        }

        // if we have (for example) 4 uploads, each able to reach 100%, our end goal will be 400%
        var percentage_max = num_file_uploads * 100;
        var percentage_real = (percentage_done / percentage_max) * 100;

        // let's cap this at 95%, since we aren't accounting for the submissions themselves
        percentage_real = 0.95 * percentage_real;

        return percentage_real;
    },

    /**
     * When a user clicks to upload a form, or from the left hand nav,
     * this gets called
     */
    start_upload(options, hide_beginning_upload_msg = false) {
        options = $f.utils.setup_options_callbacks(options);

        this._upload_error_count = 0; // start off with a clean slate

        // In case start_upload() is called multiple times, let's keep track of the success/error message and just
        // show the last one we saved.
        this._active_upload_success_msg = options.msg;
        this._active_upload_error_msg = options.error_msg;

        const _complete = () => {
            showUploadProgress();
            $f.application.vent.trigger('mobile:set_num_uploads');

            this._active_upload_success_msg = null;
            this._active_upload_error_msg = null;
            $f.mobile.notifications.showUploadSuccessMessage = true;
        };

        const _success = () => {
            if ($f.mobile.notifications.showUploadSuccessMessage) {
                showSuccessMessage(this._active_upload_success_msg || _t('mobile_message_start_upload_success'));
            }

            // invalidate our tasks caches, as they have likely changed after whatever we just uploaded
            $f.mobile.cache.void_ttl_by_cache_substring('mobile_tasks__');

            if (this.get_num_pending_submission_uploads().length) {
                // if we finished but still have pending uploads, someone must have added a new one mid-process
                return this.start_upload(options, true);
            }

            options.success();
            _complete();
        };

        const _error = () => {
            this.for_each_pending_upload_structure((submissions) => {
                submissions.set_is_uploading(false);
            });

            showErrorMessage(this._active_upload_error_msg || _t('mobile_message_start_upload_error'), true);

            options.error();
            _complete();
        };

        if (!hide_beginning_upload_msg) {
            showBeginningUploadMessage();
        }

        try {
            $f.mobile.pending_form_submissions.upload(function () {
                $f.mobile.pending_task_submissions.upload(function () {
                    $f.mobile.new_tasks.upload(function () {
                        $f.mobile.new_project.upload(function () {
                            $f.mobile.fast_tasks.upload(function () {
                                $f.mobile.form_drafts.upload(_success, _error);
                            }, _error);
                        }, _error);
                    }, _error);
                }, _error);
            }, _error);
        } catch (e) {
            console.error(e);
            $f.mobile.logging.capture_exception(e);
            _error();
        }
    }
};

/**
 * Our <input type="file" /> manager, so that individual Marionette views don't have to worry about keeping
 * these elements alive within their own views on mobile web.
 */
mobile.uploads.web_filestore = {
    _filestore: {
        /* uuid: File */
    }, // we will keep files in memory here
    allowed_file_types: '', // file types accepted by the input field
    /**
     * Open the file picker and let the user select a file. If the user cancels, the callback will not be called.
     *
     * @param  {object}   options                       Options for prompting for the files.
     * @param  {boolean}   options.allowed_file_types    The filetypes that should be accepted
     * @param  {boolean}   options.allow_multiple       Should we allow the user to select multiple files?
     * @param  {Function}  callback                     Function to be called upon success of selecting an image
     */
    pick_new_file: function (options, callback) {
        callback = callback || function () {};
        this.allowed_file_types = options.allowed_file_types || '';
        options.is_media = options.is_media || false;

        var this_ = this;
        var $filestore = $('.web_filestore');
        var new_uuid = createUUID();

        // First, create a new file input
        $filestore.append(
            `<input type="file" data-uuid="${new_uuid}" accept="${options.allowed_file_types}" ${
                options.allow_multiple ? 'multiple' : ''
            }/>`
        );

        var $new_input = $filestore.find(`[data-uuid=${new_uuid}]`);

        $new_input.on('change', function (event) {
            var raw_files = event.target.files;
            var files = new ZenputFiles();
            var is_valid = _.some(raw_files, function (raw_file) {
                return this_._is_valid_file_type(raw_file, options.is_media);
            });

            if (!is_valid) {
                showErrorMessage(_t('mobile_webfilestore_message_error'));
                return;
            }

            _.each(raw_files, function (raw_file, i) {
                var file = new ZenputFile({
                    id: createUUID(i),
                    raw_file: raw_file
                });

                this_._filestore[file.id] = file;
                files.add(file);
            });

            callback(files);
        });

        $new_input.trigger('click');
    },

    get_file: function (id) {
        return this._filestore[id];
    },

    set_file: function (file) {
        this._filestore[file.id] = file; // ZenputFile
    },

    /** NOTE: @author:halah because firefox file picker defaults to all files instead of what we determine the input
     * accepts, we have to validate file type.
     * Since the the file type for media files (image & videos) is: image/jpg & video/mp3 respectively
     * and the allowed_file_types is 'image/*' &  'video/*', we extract 'image' & 'video' from the file type & look for
     * them in the allowed_file_types
     */
    _is_valid_file_type: function (file, is_media) {
        is_media = is_media || false;

        if (file.type === '') {
            return false;
        }
        if (!is_media) {
            return this.allowed_file_types.indexOf(file.type) !== -1;
        }
        return this.allowed_file_types.indexOf(file.type.substring(0, 5)) !== -1; // image & video file types
    }
};

/**
 * This handles keeping state of history while moving through pages
 */
mobile.nav = {
    _views: [],
    _options: [],
    _scroll_pos: [],
    // Records the direction of the most recent nav action so that the next
    // page's onShow can attribute itself to forward / back.
    // Set by show() / go_back(); cleared on read by consume_last_nav_direction().
    _last_nav_direction: null,

    consume_last_nav_direction: function () {
        const direction = this._last_nav_direction;
        this._last_nav_direction = null;
        return direction;
    },

    show: function (mobile_view, options) {
        if (this._last_nav_direction == null) {
            this._last_nav_direction = 'forward';
        }

        // MobileDashboard is a special case where we continue calling .show() on the same page
        // to self navigate. So we do need to add this to the view history
        var allow_duplicate_views = mobile_view === MobileDashboard;

        if (allow_duplicate_views || this._views[this._views.length - 1] != mobile_view) {
            this._views.push(mobile_view); // do not add duplicate view types to history
        } else {
            this._options.pop(); // but we should reset the options if its the same view
            this._scroll_pos.pop();
        }
        this._options.push(options);

        if (this._scroll_pos.length) {
            this.update_scroll_position();
        }
        this._scroll_pos.push(0);

        var view = new mobile_view(options);

        // Allows us to redirect to messaging for users who just logged in
        if (!$f.active_company.is_subscription_valid()) {
            view = new StatusPage({ messaging: $f.active_company.get('messaging') });
        }

        if ($f.application.content.currentView && $f.application.content.currentView.r_main_content) {
            $f.application.content.currentView.r_main_content.show(view);
        } else {
            $f.application.content.show(view);
        }
    },

    /**
     * Method to show dialogs, such as the account picker or the signature dialog. This puts dialogs in an overlay
     * for the web app only so that any important file <input> fields holding pending uploads are not destroyed.
     *
     * @param  {Backbone.Marionette.View} view The dialog View to display
     */
    show_modal: function (view_class, options) {
        options = options || {};
        options.is_modal = true;

        $f.application.content_overlay.show(new view_class(options));
    },

    go_back: function (disable_transition) {
        this._last_nav_direction = 'back';

        this._views.pop();
        this._options.pop();
        this._scroll_pos.pop();

        if (this._views.length == 0) {
            // This shouldn't ever happen
            $f.application.content.currentView.r_main_content.show(new MobileDashboard({ is_top_level_page: true }), {
                is_historic_view: true
            });
            return;
        }

        var last_index = this._views.length - 1;
        var mobile_view_class = this._views[last_index];
        var view_options = this._options[last_index] || {};
        view_options.is_historic_view = true;

        $f.application.content.currentView.r_main_content.show(new mobile_view_class(this._options[last_index]), {
            is_historic_view: true,
            disable_transition: disable_transition
        });
    },
    /**
     * Method to go_back to a view in the _views stack
     *
     * @param  {Backbone.Marionette.View} view_class the class of the view to go back to
     * @param  {dict} options options to be passed to the view when it's displayed
     */
    go_back_to_view: function (view_class, options) {
        this._last_nav_direction = 'back';

        if (this._views.length == 0) {
            // This shouldn't ever happen
            $f.application.content.currentView.r_main_content.show(new MobileDashboard({ is_top_level_page: true }), {
                is_historic_view: true
            });
            return;
        }

        var views_length = this._views.length - 1;

        for (var top_view_index = views_length; top_view_index >= 0; top_view_index--) {
            var current_view_class = this._views[top_view_index];
            var is_view = current_view_class === view_class;

            if (is_view) {
                break;
            }

            this._views.pop();
            this._options.pop();
        }

        $f.application.content.currentView.r_main_content.show(new view_class(options), {
            is_historic_view: true,
            disable_transition: true
        });
    },
    _get_active_page_scrollable: function () {
        var $tabbed_page_scrollable = $('.main_content:not(.stale) .tabbed_layout_page.active .page_scrollable');
        if ($tabbed_page_scrollable.length) {
            return $tabbed_page_scrollable;
        }

        return $('.main_content:not(.stale) .page_scrollable');
    },

    update_scroll_position: function () {
        var scrolltop = this._get_active_page_scrollable().scrollTop();

        if ($f.utils.is_nil(scrolltop)) {
            return;
        }

        var current_index = $f.mobile.nav._scroll_pos.length ? $f.mobile.nav._scroll_pos.length - 1 : 0;
        $f.mobile.nav._scroll_pos[current_index] = scrolltop;
    },

    restore_scroll_position: function () {
        this._get_active_page_scrollable().scrollTop(this._scroll_pos[this._scroll_pos.length - 1]);
    },

    /**
     * This will extend the options given into the current views
     * options.  This way we can use proper history to save states
     */
    add_options: function (options) {
        $.extend(this._options[this._options.length - 1], options);
    },

    get_current_options: function () {
        return this._options[this._options.length - 1] || {};
    },

    /**
     * Check to see if the most recently displayed view is a "top level" page (i.e., the first view in history).
     * @return {Boolean} True if the last view is the first in history, false if not.
     */
    is_current_view_top_level_page: function () {
        return this._views.length == 1;
    },

    is_current_page_historic: function () {
        var last_options = this._options[this._options.length - 1] || {};
        return Boolean(last_options.is_historic_view);
    },

    clear: function () {
        this._views = []; // this is causing problems with the save draft bug
        this._options = []; // this is causing problems with the save draft bug
        this._scroll_pos = [];
        $f.application.vent.trigger('nav:clear');
    },

    /**
     * This will add a backdrop when modal is opened
     * @param  dict options options such as opacity or animation duration
     * @param callback function to be called when click event is triggered outside modal_touch_block
     */
    show_modal_touchblock: function (options, callback) {
        callback = callback || function () {};
        options = options || {};

        $('.modal_touchblock')
            .css({ display: 'block', opacity: 0 })
            .velocity({ opacity: 1 }, { duration: options.duration })
            .on('click.modal_touchblock', function () {
                callback();
            });
    },

    /**
     * This will hide modal_touchblock when modal is closed and will remove modal_touch_block click event
     * @param  dict options options such as opacity or animation duration
     */
    hide_modal_touchblock: function (options) {
        options = options || {};

        $('.modal_touchblock')
            .velocity({ opacity: 0 }, { display: 'none', duration: options.duration })
            .off('click.modal_touchblock');
    }
};

/**
 * A simple Key/Value mechanism for storing local
 * user data that should persist
 */
mobile.settings = {
    set: function (key, value, callback) {
        $f.mobile.storage.get('settings', function (settings) {
            settings = settings || {};
            settings[key] = value;
            $f.mobile.storage.set('settings', settings, callback);
        });
    },

    get: function (key, callback) {
        $f.mobile.storage.get('settings', function (settings) {
            if (!settings || typeof settings === 'undefined' || !(key in settings)) {
                callback(null);
                return;
            }
            callback(settings[key]);
        });
    },

    remove: function (key, callback) {
        $f.mobile.storage.get('settings', function (settings) {
            settings = settings || {};
            delete settings[key];
            $f.mobile.storage.set('settings', settings, callback);
        });
    },

    clear: function (callback) {
        this.clear_last_login();
        $f.mobile.storage.set('settings', null, callback);
    },

    update_last_login: function (callback) {
        $f.mobile.settings.set('last_login', new Date(), callback);
    },

    get_last_login: function (callback) {
        $f.mobile.settings.get('last_login', callback);
    },

    clear_last_login: function (callback) {
        $f.mobile.settings.set('last_login', null, callback);
    },

    /**
     * Get the platform type (i.e., Android, iOS, or Windows) that we're on (not including the version number).
     * Formatting options are available because of pre-existing inconsistencies when logging/reporting the plaform.
     *
     * @param   {string}  _default    The default value if the platform type can't be determined (by deafult, null)
     * @param   {boolean} uppercase   Should we uppercase the response?
     *
     * @returns {string}              The current platform type: Android, iOS, or Windows
     */
    get_platform_type: function (_default, uppercase) {
        _default = _default || null;

        var platform = null;
        if (isWeb()) {
            // We want it to be web if used from android, ios or windows browser
            platform = 'Web';
        } else {
            if (isAndroid()) {
                platform = 'Android';
            } else if (isIos()) {
                platform = 'iOS';
            } else if (isWindows()) {
                platform = 'Windows';
            }
        }

        if (!platform) {
            return _default;
        }

        if (!uppercase) {
            return platform.toLowerCase();
        }

        return platform;
    },

    get_release_details: function () {
        if (!isNative()) {
            const platformType = this.get_platform_type();
            return {
                device: null,
                platform_name: platformType === 'web' ? 'mobile web' : platformType,
                platform_version: null,
                release_native: null
            };
        }

        return {
            device: `${CordovaPlugins().device.manufacturer} ${CordovaPlugins().device.model}`,
            platform_name: CordovaPlugins().device.platform,
            platform_version: CordovaPlugins().device.version,
            release_native: CordovaPlugins().BuildInfo.version
        };
    },

    /**
     * Updates the user agent with information about this release and platform,
     *
     * @param {function} callback
     */
    patch_user_agent: function (callback) {
        callback = callback || function () {};

        if (isWeb() || isWindows()) {
            callback();
            return;
        }

        CordovaPlugins().UserAgent.get(function (user_agent) {
            // UserAgent modifications last until app restart but are not cleared across page refreshes.
            if (user_agent.indexOf(' zenput') > -1) {
                user_agent = user_agent.split(' zenput')[0]; // original UA
            }

            const { release_native, platform_name, platform_version } = $f.mobile.settings.get_release_details();

            CordovaPlugins().UserAgent.set(
                `${user_agent} zenput:app_version=${release_native};platform_type=${platform_name};platform_version=${platform_version}`,
                callback,
                callback
            );
        }, callback);
    },

    init_wootric: function () {
        // just make sure we're online
        if (!isConnected()) {
            return;
        }

        // Make the call!
        $.ajax({
            url: 'https://disutgh7q0ncc.cloudfront.net/beacon.js',
            dataType: 'script',
            success: function () {
                if (isDebuggingEnabled()) {
                    return;
                }
                if (!window.wootric) {
                    return;
                }
                if (!$f.active_user.is_logged_in()) {
                    return;
                }
                if (!$f.active_company.is_subscription_valid()) {
                    return;
                }

                // If the user has a zenput or crunchtime email do not init
                var user_email = $f.active_user.get('email');
                if (user_email.indexOf('@zenput.com') !== -1 || user_email.indexOf('@crunchtime.com') !== -1) {
                    return;
                }

                // window.wootric_survey_immediately = true; // for testing only, comment out for production
                // window.wootric_no_surveyed_cookie = true; // for testing only, comment out for production

                // If the user doesn't have a date_redeemed, they weren't invited, so we'll fall back to date_joined
                var date_signup = $f.active_user.get('date_redeemed')
                    ? $f.active_user.get('date_redeemed').$date
                    : $f.active_user.get('date_joined').$date;

                window.wootricSettings = {
                    email: user_email,
                    external_id: $f.active_user.id,
                    created_at: date_signup / 1000, // seconds, not ms
                    account_token: 'NPS-e4b72617',
                    modal_position: 'top', // iOS has an issue with default 'bottom' because of the way we disable
                    // native scroll when keyboard opens. Using 'top' as a quick fix.
                    language: localization.locale_to_lang($f.active_user.get('userprofile__locale')).toUpperCase(),
                    wootric_recommend_target: _t('wootric_recommend_target'),
                    properties: {
                        OS: $f.mobile.settings.get_platform_type('desktop'),
                        platform: 'mobile',
                        Permission: $f.active_user.get('role').toLowerCase()
                    }
                };

                // @todo: @author:gustavo We only really use followup_questions_list but we want to make sure wootric
                // web admin doesn't overwrite with custom values without translations
                window.customMessages = {
                    followup_question: _t('wootric_default_followup_question'),
                    followup_questions_list: {
                        detractor_question: _t('wootric_followup_detractor_question'),
                        passive_question: _t('wootric_followup_passive_question'),
                        promoter_question: _t(
                            $f.flags.get_flag('rebrand')
                                ? 'wootric_followup_promoter_question'
                                : 'wootric_followup_promoter_question_old'
                        )
                    },
                    placeholder_text: _t('wootric_default_placeholder_text'),
                    placeholder_texts_list: {
                        detractor_text: _t('wootric_default_placeholder_text'),
                        passive_text: _t('wootric_default_placeholder_text'),
                        promoter_text: _t('wootric_default_placeholder_text')
                    }
                };

                // This is due to having reverse engineered beacon.js. It looks for the existence of a var named Wootric
                // in the window.parent scope.
                // @todo @note: for more customization see: http://docs.wootric.com/javascript/#custom-thank-you-settings
                // We can't predetermine messages further without turning on unwanted features like social sharing.
                window.Wootric = {
                    thankYouMessages: {
                        thank_you_main: _t('wootric_default_thankyou_text'),
                        thank_you_main_list: {
                            detractor_thank_you_main: _t('wootric_default_thankyou_text'),
                            passive_thank_you_main: _t('wootric_default_thankyou_text'),
                            promoter_thank_you_main: _t('wootric_default_thankyou_text')
                        }
                    }
                };

                window.wootric('run');
            }
        });
    },

    // Get timezone from user device and update in db
    set_user_time_zone: function () {
        var timezone = jstz.determine();
        if (!$f.active_user.is_logged_in()) {
            return;
        }

        if ($f.active_user.get('time_zone') != timezone.name()) {
            $f.active_user.set('time_zone', timezone.name());
            $f.active_user.save();
        }
    }
};

mobile.logging = {
    /**
     * Log a message to Sentry.
     * This should only be used for unexpected errors, as we will receive these in Sentry and/or
     * in email.
     *
     * WARNING: Please do not put unique data into msg. Each msg shows up as
     * a different event in Heap, making them hard to track if the same logs
     * have different msgs. Instead, define relevant data in obj.
     */
    log_error: function (msg, obj, log_to_console = true) {
        obj = obj || {};

        if (typeof obj !== 'object') {
            obj = { message: obj }; // make sure this is always an object so appears ok in sentry
        }

        if (log_to_console) {
            console.error(msg, obj);
        }

        const sentry = this.get_sentry();
        sentry.withScope((scope) => {
            scope.setExtras(obj);
            sentry.captureMessage(msg);
        });
    },

    capture_exception: function (e) {
        this.get_sentry().captureException(e);
    },

    // @todo:capacitor @jira:ZP-12868 @sentry/capacitor isn't reporting errors properly, so we'll use @sentry/browser for now
    get_sentry: () => SentryBrowser
};

/**
 * Handles Push Notifications
 * @todo this object is deprecated and can be removed after removing the push_registration_refactor flag
 * @deprecated
 */
mobile.push_notifications = {
    LAST_PUSH_ID_KEY: 'last-push-id',

    // Sender ID from Firebase API Console
    FCM_SENDDER_ID_DEV: '192616215583',
    FCM_SENDDER_ID_PROD: '241335660919',

    get_device_id: function (callback) {
        $f.mobile.settings.get('push_notifications_device_id', callback);
    },
    set_device_id: function (id, callback) {
        $f.mobile.settings.set('push_notifications_device_id', id, function () {
            $f.mobile.settings.set('last_push_notification_registration', new Date(), callback);
        });
    },

    get_last_registration: function (callback) {
        $f.mobile.settings.get('last_push_notification_registration', function (date) {
            if (!date) {
                if (callback) {
                    callback(null);
                }
                return;
            }
            if (callback) {
                callback(new Date(date));
            }
        });
    },

    /**
     * This gets called after we hit our Push Notifications provider.  We should
     * record the device ID on this device, as well as with the server
     */
    registration_callback: function (status) {
        if (!status || !status.pushToken) {
            return;
        }
        if (!$f.active_user.is_logged_in()) {
            return;
        }

        $f.mobile.push_notifications.get_last_registration(function (last_registration) {
            // only attempt to re-register with our servers once a day
            if (last_registration && new Date() - last_registration < 86400000) {
                return;
            }

            var push_token = status.pushToken;

            // go hit the Server and save this data.
            var device = new UserDevice({
                id: push_token,
                name: $f.mobile.settings.get_platform_type('Unknown', true)
            });

            device.save(
                {},
                {
                    success: function () {
                        $f.mobile.push_notifications.set_device_id(push_token);
                    },
                    error: function () {
                        // @author:david oh well, nothing we can do
                    }
                }
            );
        });
    },

    /**
     * To be called at logout
     */
    unregister_device: function () {
        this.get_device_id(function (device_id) {
            var device = new UserDevice({ id: device_id });
            device.destroy(); // hit our server and remove
            $f.mobile.settings.set('push_notifications_device_id', null);
            $f.mobile.settings.set('last_push_notification_registration', null);
            if (isNative()) {
                CordovaPlugins().pushwoosh.unregisterDevice();
            }
        });
    },

    /**
     * This will initialize our device to receive push notifications.
     * This will ONLY run if you are logged in and on a native app.
     *
     * Once we receive a device token (id) from our provider, we send
     * it to our servers for storage.
     *
     * This runs on a delay for a reason, so we dont hold things up
     */
    initialize: function () {
        var this_ = this;
        var sender_id = process.env.DEPLOY_ENV === 'production' ? this.FCM_SENDDER_ID_PROD : this.FCM_SENDDER_ID_DEV;

        if (isWeb()) {
            return;
        }

        /**
         * This is fired when the user taps on the notification (even when the app is force-closed; the app
         * will resume and then fire this).
         */
        document.removeEventListener('push-notification');
        document.addEventListener('push-notification', function (event) {
            this_._handle_push_tapped_event(event);
        });

        /**
         * This is fired when the user recieves a notification
         */
        document.removeEventListener('push-receive');
        document.addEventListener('push-receive', function (event) {
            this_._handle_push_recieved_event(event);
        });

        // @author:david A note about Windows:
        // Unlike iOS/Android, Windows push notifications will only work with apps signed for the Windows Store.
        // This means that even for development builds, in order to test pushes, the app needs to be signed with
        // our Windows Store certificate (or "associated" with the app store in Visual Studio). Pushes to production
        // will still only target production builds, however, since we specify a different appid (process.env.PUSHWOOSH_APP_ID) below.

        // Initialize Pushwoosh. This will trigger all pending push notifications on start.
        CordovaPlugins().pushwoosh.onDeviceReady({
            appname: 'Zenput',
            appid: process.env.PUSHWOOSH_APP_ID,
            projectid: sender_id
        });

        if (isAndroid()) {
            // Without this, new pushes will remove previous ones
            CordovaPlugins().pushwoosh.setMultiNotificationMode(
                function () {},
                function (err) {
                    $f.mobile.logging.log_error('Failed to setMultiNotificationMode for Pushwoosh', err);
                }
            );
        }

        if (!$f.active_user.is_logged_in()) {
            return;
        }

        CordovaPlugins().pushwoosh.registerDevice(
            (status) => {
                $f.mobile.push_notifications.registration_callback(status);
            },
            () => {
                console.error('Failed to register for push notifications');
            }
        );
    },

    /**
     * Parse the push notification event object
     *
     * @param {Event} event The push notification event
     * @returns { url, push_id }
     */
    _parse_push_notification_event: function (event) {
        var push_data = {};

        try {
            push_data = event.notification.userdata || {};
        } catch (e) {
            // keep default {}
        }

        return push_data;
    },

    /**
     * Handle when a user taps a push notification
     */
    _handle_push_tapped_event: function (event) {
        var this_ = this;
        var push_data = this._parse_push_notification_event(event);
        var url = push_data.url;
        var push_id = push_data.push_id;

        if (!url) {
            return;
        }

        if (!isIos() || !push_id) {
            $f.mobile.redirect(url);
            return;
        }

        // @jira:ZP-3379 @jira:ZP-3376 iOS sends multiple events for the same push notification. We'll block
        // duplicate notifications by checkign for a duplicate timestamp. We can't track this in memory because we
        // sometimes refresh the page when redirecting.
        $f.mobile.storage.get(
            this.LAST_PUSH_ID_KEY,
            function (last_id) {
                if (isIos()) {
                    if (last_id && last_id === push_id) {
                        return;
                    }

                    $f.mobile.storage.set(
                        this_.LAST_PUSH_ID_KEY,
                        push_id,
                        function () {
                            $f.mobile.redirect(url);
                        },
                        function (error) {
                            $f.mobile.logging.log_error('failed to set last push ID', { error: error });
                            $f.mobile.redirect(url);
                        }
                    );
                } else {
                    $f.mobile.redirect(url);
                }
            },
            function (error) {
                $f.mobile.logging.log_error('failed to get last push ID', { error: error });
                $f.mobile.redirect(url);
            }
        );
    },

    /**
     * Handle when we've received a push notification
     */
    _handle_push_recieved_event: function (event) {
        var push_data = this._parse_push_notification_event(event);
        var url = push_data.url;

        if (!url) {
            return;
        }

        // detecs if it's a comments push notification
        if (url.indexOf('comments') === -1) {
            return;
        }

        $f.mobile.unread_notifications_count.fetch();
    }
};

mobile.notifications = {
    // allow midstream turning off of upload success message. when toggled off, it turns back on after one success callback.
    showUploadSuccessMessage: true,
    globalNotificationView: null, // our global notification view that is used for showing notifications when the leftnav is open
    closeToastTimeout: null,
    activeToastInfo: null,
    // This allow using the function in static/js/shared folder
    showErrorMessage,
    scheduleTaskDueSoonLocalNotifications,

    onLocalNotificationActionPerformed: (event) => {
        const redirectUrl = event.notification?.extra?.redirectUrl;

        if (isNative()) {
            $f.mobile.redirect(redirectUrl);
        } else {
            window.location.href = redirectUrl;
        }
    },

    cancelFutureLocalNotificationForTaskId: (taskId) => {
        LocalNotifications.cancel({
            notifications: [{ id: taskId }]
        });

        if (!isReleaseBuild()) {
            // eslint-disable-next-line no-console
            console.log('Cancelled future local notification: ', taskId);
        }
    },

    cancelAllFutureLocalNotifications: async () => {
        const pending = await LocalNotifications.getPending();
        if (pending.notifications.length) {
            await LocalNotifications.cancel(pending);
        }
        if (!isReleaseBuild()) {
            // eslint-disable-next-line no-console
            console.log('Cancelled all future local notifications');
        }
    }
};

/**
 * Our JS cache, which sits on top of our cache 2.0 (@see backbone.fetch-cache-mobile.js).
 *
 * This is essentially a wrapper for $f.mobile.storgae.get()/set() that does some extra caching in JS memory on
 * top of local storage caching. When get() or js_load() are called, the results are stored here for
 * more performant fetches in the future. When local caches are updated, they are updated here as well
 * (if they are currently loaded).
 */
mobile.cache = {
    MAX_JS_CACHES: 100, // only store a max of 100 caches concurrently in JS memory
    GARBAGE_COLLECT_EXEMPT_WHITELIST: ['active_user', 'active_company'], // always keep these caches

    _caches: {}, // [{ <cache_key>: <cache contents as string> }]
    _metadata: {}, // [{ <cache_key>: { <option_key>: <option_value> } }] - global metadata/settings

    /**
     * We use this denylist to keep track of model IDs that we should not be accepted from server fetches. This is to
     * address situations where our servers are still working after we submit changes and fetch down stale results
     * (e.g., submit form --> form completes task on worker --> we fetch tasks list before that completes and end
     * up re-downloading the already submitted form task).
     */
    denylist: {}, // { <key>: { <model_id>: <time denylisted> } }

    /**
     * Update a cache's value.
     */
    _set_cache_js: function (key, val) {
        if (typeof val == 'string') {
            val = JSON.parse(val);
        }

        if (typeof val === 'undefined' || val === null) {
            this.js_unload(key);
            return;
        }

        this._caches[key] = val;
        this._garbage_collect();
    },

    /**
     * Get metadata for a specified cache_key.
     */
    metadata_for: function (cache_key) {
        if (!this._metadata[cache_key]) {
            this._metadata[cache_key] = {};
        }

        return this._metadata[cache_key];
    },

    /**
     * Load a given cache from local storage into memory, for performance.
     */
    js_load: function (key, callback) {
        callback || (callback = function () {});
        var this_ = this;
        $f.mobile.storage.get(
            key,
            function (results) {
                this_._set_cache_js(key, results);
                callback();
            },
            callback
        );
    },

    /**
     * Remove a cache from memory.
     */
    js_unload: function (key) {
        this._metadata[key] = {};
        delete this._caches[key];
    },

    /**
     * Clear all JS caches.
     */
    js_unload_all: function () {
        this._caches = {};
    },

    /**
     * In addition to saving to local storage, update our JS cache if we have it loaded.
     */
    set: function (key, val, success, error) {
        success || (success = function () {});
        error || (error = function () {});

        var _success = success;
        success = function () {
            return _success.apply(this, arguments);
        };

        // @author:david because of our cacheable model (@see backbone.fetch-cache-mobile.js), this needs to run
        // first, before we save to disk, so that in the case of someone calling model.set('foo', 'bar') and then
        // immediately model.get('foo'), the JS cache is up-to-date for the local fetch we do in get().
        this._set_cache_js(key, val);

        $f.mobile.storage.set(key, val, success, error);
    },

    /**
     * If we have the cache loaded into memory, return those results. Otherwise, get them from local
     * storage (and load them into memory for next time).
     */
    get: function (key, success, error) {
        success || (success = function () {});
        error || (error = function () {});
        var this_ = this;

        if (this._caches[key]) {
            success(this_._caches[key]);
            return;
        }

        var _success = success;
        success = function (results) {
            this_._set_cache_js(key, results);
            return _success.apply(this, arguments);
        };

        $f.mobile.storage.get(key, success, error);
    },

    /**
     * Remove an entry from both the JS cache and local storage.
     */
    clear: function (key, callback) {
        callback || (callback = function () {});

        this.js_unload(key); // remove from JS cache
        $f.mobile.storage.remove(key, callback, callback); // remove from local
    },

    /**
     * Get a list of cache keys that match a specified substring or list of substrings.
     *
     * @param  {array|string}   substrings  The substring(s) to match against
     * @param  {Function}       callback    The callback to be called with the results
     */
    _get_keys_by_substring: function (substrings, callback) {
        substrings = substrings instanceof Array ? substrings : [substrings];

        $f.mobile.storage.keys(function (keys) {
            var keys_of_interest = $.grep(keys, function (key) {
                // Build a new array of keys to remove
                var matching_substrings = $.grep(substrings, function (substring) {
                    return key.indexOf(substring) > -1;
                });
                return matching_substrings.length > 0;
            });

            callback(keys_of_interest);
        });
    },

    /**
     * Remove any keys in $f.mobile.storage matching the provided substring(s).
     * This is mostly used to clear out old cache keys that we no longer use.
     *
     * @param  {array|string}   substrings  The substring or substrings to search for in NativeStorage and remove
     * @param  {Function}       callback    To be run upon completion
     */
    clear_by_substring: function (substrings, callback) {
        callback || (callback = function () {});
        if (!_.isArray(substrings)) {
            substrings = [substrings];
        }
        var this_ = this;

        this._get_keys_by_substring(substrings, function (keys_to_remove) {
            var _clear = function () {
                // Recursively remove each key from disk
                if (keys_to_remove.length == 0) {
                    callback();
                    return;
                }

                this_.clear(keys_to_remove.pop(), _clear);
            };
            _clear();
        });
    },

    /**
     * Remove any IDs passed in from any caches with a matching cache key substring. Also add these IDs to a denylist
     * so we can strip them from server responses later on (optional).
     *
     * @author:david using cache key substrings was originally meant to be a quick cleanup thing. If we don't switch
     * entirely to something like SQLite, maybe we should start using cache subtypes or something.
     *
     * @param  {object}         options                     Settings to pass in
     * @param  {string|array}   options.ids                 An array of IDs to be removed from the cache(s)
     * @param  {string|array}   options.cache_key_substrs   A string for looking up caches to remove IDs from
     * @param  {boolean}        options.add_to_denylist    (Default true) Should we add these IDs to our denylist so we strip
     *                                                      them out whenever we fetch?
     * @param  {function}       options.callback            A callback to be fired after the deed is done
     */
    remove_ids_by_cache_substring: function (options) {
        options.callback = options.callback || function () {};
        options.ids = options.ids instanceof Array ? options.ids : [options.ids];
        options.cache_key_substrs =
            options.cache_key_substrs instanceof Array ? options.cache_key_substrs : [options.cache_key_substrs];
        options.add_to_denylist = options.add_to_denylist !== false; // default to true
        var this_ = this;

        // First, let's add these keys to our denylist so we don't fetch them again
        if (options.add_to_denylist) {
            _.each(options.cache_key_substrs, function (key_substr) {
                var denylist = $f.mobile.cache.denylist[key_substr] || {};

                _.each(options.ids, function (id) {
                    denylist[id] = new Date().getTime();
                });

                $f.mobile.cache.denylist[key_substr] = denylist;
            });
        }

        this._get_keys_by_substring(options.cache_key_substrs, function (keys_to_check) {
            var _remove_ids = function () {
                // Recursively remove each key from disk
                if (!keys_to_check || !keys_to_check.length) {
                    options.callback();
                    return;
                }

                var key = keys_to_check.pop();

                this_.get(key, function (data) {
                    if (!data || !data.length) {
                        return _remove_ids();
                    }

                    var initial_length = data.length;

                    // remove each ID from this cache
                    _.each(options.ids, function (id) {
                        data = _.without(data, _.findWhere(data, { id: id }));
                    });

                    if (data.length === initial_length) {
                        return _remove_ids(); // no need to overwrite anything if nothing's been removed
                    }

                    this_.set(key, data, function () {
                        _remove_ids();
                    });
                });
            };
            _remove_ids();
        });
    },

    /**
     * Update our cache with a new version of a model
     *
     * @param  {object}         options                     Settings to pass in
     * @param  {number}         options.item_id             The ID of the item to update in the caches
     * @param  {function}       options.update_fn           A function to perform the update; must return the modified version of the object
     * @param  {string|array}   options.cache_key_substrs   A string for looking up caches to remove IDs from
     * @param  {function}       options.callback            A callback to be fired after the deed is done
     */
    update_cache_item: function (options) {
        options = options || {};
        var this_ = this;
        var item_id = options.item_id;
        var update_fn = options.update_fn;
        var cache_key_substrs =
            options.cache_key_substrs instanceof Array ? options.cache_key_substrs : [options.cache_key_substrs];
        var callback = options.callback || function () {};

        this._get_keys_by_substring(cache_key_substrs, function (keys_to_check) {
            var _update_ids = function () {
                // Recursively update each key from disk
                if (!keys_to_check || !keys_to_check.length) {
                    callback();
                    return;
                }

                var key = keys_to_check.pop();

                this_.get(key, function (data) {
                    if (!data || !data.length) {
                        return _update_ids(); // continue; no need to overwrite anything if nothing's been changed
                    }

                    var has_updated = false;

                    var updated_data = _.map(data, function (item) {
                        if (item.id !== item_id) {
                            return item; // no update
                        }

                        var result = update_fn(item);

                        if (!result) {
                            throw new Error('Programmer Error: update_fn must return new value');
                        }

                        has_updated = true;
                        return JSON.parse(JSON.stringify(result)); // Using JSON.parse + JSON.stringify instead of $f.utils.deepclone as the last return a full copy of a backbone object instead of just of the instance.  Also, new object to avoid pass-by-reference issues with the JS cache.
                    });

                    if (!has_updated) {
                        return _update_ids(); // continue; no need to overwrite anything if nothing's been changed
                    }

                    this_.set(key, updated_data, function () {
                        _update_ids();
                    });
                });
            };
            _update_ids();
        });
    },

    /**
     * Invalidate any caches matching the provided substring by resetting their TTL to 0.
     *
     * @param  {string|array} cache_key_substrs The substring(s) to look for when deciding what caches to invalidate
     */
    void_ttl_by_cache_substring: function (cache_key_substrs) {
        cache_key_substrs = cache_key_substrs instanceof Array ? cache_key_substrs : [cache_key_substrs];
        var this_ = this;

        return new Promise((resolve) => {
            this._get_keys_by_substring(cache_key_substrs, function (keys_to_check) {
                _.each(keys_to_check, function (key) {
                    // the beginning of time should be old enough to invalidate any TTL
                    this_.metadata_for(key).last_cache_refill = new Date(0);
                });

                resolve();
            });
        });
    },

    /**
     * Make sure we're not storing too much in JS memory. If we're over our MAX_JS_CACHES,
     * unload from oldest to newest.
     */
    _garbage_collect: function () {
        var keys = Object.keys(this._caches);

        while (keys.length > this.MAX_JS_CACHES) {
            var key = keys.pop();

            if (!_.contains(this.GARBAGE_COLLECT_EXEMPT_WHITELIST, key)) {
                this.js_unload(key);
            }
        }
    }
};

var Router = Backbone.Marionette.AppRouter.extend({
    routes: {
        home: 'default_route',
        notifications: 'show_notifications',
        'forms/id/:rdbms_id/brandhub': 'show_brandhub',
        'id/:rdbms_id': 'show_form_id', // @deprecated, use 'forms/id/:rdbms_id' instead
        'forms/id/:rdbms_id': 'show_form_id', // mobile only, link not supported on webapp
        'reports/form_id/:submission_id': 'show_submission',
        'reports/form_id/:submission_id/comments/:comment_id': 'show_submission_with_comments',
        'sensors/detail/:id': 'show_sensor_details',
        tasks: 'show_tasks',
        projects: 'show_projects',
        add_tasks: 'show_add_tasks',
        'tasks/id/:id': 'show_single_task',
        'tasks/id/:task_id/comments/:comment_id': 'show_single_task_with_comments',
        'follow_up_actions/id/:id': 'show_single_follow_up_action',
        forms: 'show_forms',
        'settings/notifications': 'show_notification_settings',
        'settings/profile': 'show_profile_settings',
        logout: 'logout',
        send_data: 'send_data',
        'debug/enable_weinre': 'enable_weinre',
        'locations/id/:id/:active_tab_id': 'show_account',
        noop: 'noop',
        '*actions': 'default_route'
    },

    default_route: function (actions) {
        if ($f.application.content.currentView && !$f.application.content.currentView.isClosed) {
            return; // @jira:ZP-3379 something is sometimes causing the app to reload the dashboard after redirecting on iOS
        }

        this._perform_route();
    },

    /**
     * Perform the default route or specific route if options are passed in.
     *
     * @param  {object}   initial_page_data A {} consisting of a `klass` key with the type of view to show as an initial page,
     *                                      plus any other properties that should be sent to said view
     * @param  {Function} callback          A function to call after the route has completed
     */
    _perform_route: async function (initial_page_data, callback) {
        callback || (callback = function () {});
        // When the app is loaded, hide the splashscreen on mobile
        var _callback = callback;
        callback = async function () {
            if (isNative()) {
                if (isWindows()) {
                    CordovaPlugins().splashscreen.hide();
                } else {
                    await SplashScreen.hide();
                }
            }
            _callback.apply(this, arguments);
        };

        // Create a global notification element that we show only when the drawer is open. Otherwise,
        // we'll usually show the notification that's inside BaseToolbarNavPageLayout.
        //
        // @note this is in _perform_route() because it is used by both the login page and the rest of the app,
        // so it needs to be available at the very start of the app.
        $f.mobile.notifications.globalNotificationView = new MobileNotificationView({
            el: $('.global_notif_wrapper .toast_notification').get(0),
            $el: $('.global_notif_wrapper .toast_notification'),
            TOP_TARGET: '0px'
        }).render();

        if ($f.active_user.is_logged_in()) {
            $f.mobile.init_app_layout(initial_page_data, callback);
            return;
        }

        if (isWeb()) {
            // try and hit the server if they are online
            isLoggedIn(
                function () {
                    $f.mobile.update_cache();
                    $f.mobile.init_app_layout(initial_page_data, callback);
                },
                $f.mobile.logout,
                true
            );

            return;
        }

        if (isNative()) {
            if (isWindows()) {
                CordovaPlugins().splashscreen.hide();
            } else {
                await SplashScreen.hide();
            }
        }

        // Their mobile session must have expired.  Log them out.
        $f.mobile.logout();
    },

    /**
     * Perform an action rather than a route. These should not redirect anywhere!
     * This will handle when to perform the action based on if we're resuming or the app has just started.
     *
     * @param  {Function} action_to_perform     A callback for what this should do
     */
    _perform_action: function (action_to_perform) {
        $f.utils.remove_url_hashbangs();

        // Determine if we are good to call the action immediately or need to load a default route first
        if (!$f.window.has_resumed) {
            this._perform_route(null, function () {
                // default route
                action_to_perform();
            });
            return;
        }

        action_to_perform();
    },

    show_notifications: function () {
        this._perform_route({
            klass: NotificationsPage
        });
    },

    show_form_id: function (rdbms_id) {
        var this_ = this;

        var success_callback = function (form_collection) {
            hideLoadingDialog();
            this_._perform_route({
                klass: MobileFormPage,
                collection: form_collection
            });
        };

        var error_callback = function () {
            // error
            hideLoadingDialog();
            this_._perform_route(null, function () {
                showErrorMessage(_t('mobile_message_show_form_id_error'));
            });
        };

        showLoadingDialog();

        // @note @author:daniel UNSAVED_CHANGES_FROM_BUILDER is set (or not) when previewing this form on the builder
        if (window.UNSAVED_CHANGES_FROM_BUILDER) {
            success_callback(new MobileFormStructure(window.UNSAVED_CHANGES_FROM_BUILDER));
            return;
        }

        $f.mobile.get_form_collection(rdbms_id, null, success_callback, error_callback);
    },

    /**
     * Show a very simple preview of a specified form, only containing the body of
     * the form - not the header or menus.
     *
     * @param  {int} rdbms_id The ID of the form to preview
     */
    show_brandhub: function (rdbms_id) {
        var this_ = this;

        $f.mobile.ajax({
            url: `/api/v1/forms/get_template/${rdbms_id}?brandhub=true`,
            dataType: 'json',
            type: 'GET',
            cache: false,
            data: { version: $f.version.id },
            success: function (form_template) {
                this_._perform_route({
                    klass: MobileFormPage,
                    collection: new MobileFormStructure(form_template)
                });
                // Add a brandhub class and the iframe header
                $('body').addClass('brandhub');
                $('body').prepend(
                    `<div class="brandhub_iframe_header"><h1>${$f.utils.template_string(
                        form_template.metadata.title
                    )}</h1></div>`
                );
            }
        });
    },

    show_tasks: function () {
        this._perform_route({
            klass: TasksPage
        });
    },

    show_projects: function () {
        this._perform_route({
            klass: ProjectsView
        });
    },

    show_add_tasks: function () {
        this._perform_route({
            klass: FastTasksPage
        });
    },

    /**
     * Given a task ID, try to find it in any of our task caches, otherwise fetch it from the server.
     *
     * @param  {int} task_id    The Task ID to find
     */
    show_single_task: function (task_id, comment_id, nav_callback) {
        var this_ = this;

        if (!task_id) {
            return showErrorMessage();
        }

        task_id = parseInt(task_id);

        $f.mobile.storage.keys(function (keys) {
            var _find_task_offline = function (callback) {
                if (!keys.length) {
                    return callback(null);
                }

                var key = keys.pop();

                if (key.indexOf(MobileTasks.cache_denylist_key) === -1) {
                    return _find_task_offline(callback);
                }

                $f.mobile.cache.get(
                    key,
                    function (tasks_json) {
                        var possible_task = _.findWhere(tasks_json, { id: task_id });

                        if (possible_task) {
                            return callback(possible_task);
                        }

                        _find_task_offline(callback);
                    },
                    function () {
                        _find_task_offline(callback); // weird, shouldn't happen. Just move on to next key
                    }
                );
            };

            _find_task_offline(function (task) {
                if (task) {
                    // if there is a task, we were able to find it on-disk
                    this_._perform_route(
                        {
                            klass: TaskDetailsPage,
                            model: new MobileTask(task),
                            active_comment_id: comment_id
                        },
                        nav_callback
                    );
                    return;
                }

                if (!isConnected()) {
                    return showOfflineMessage();
                }

                // couldn't find task on disk, so we gotta fetch it
                showLoadingDialog();

                task = new MobileTask({ id: task_id });
                task.fetch({
                    success: function () {
                        hideLoadingDialog();

                        this_._perform_route(
                            {
                                klass: TaskDetailsPage,
                                model: task,
                                active_comment_id: comment_id
                            },
                            nav_callback
                        );
                    },
                    error: function () {
                        hideLoadingDialog();
                        showErrorMessage(_t('mobile_message_show_single_task_error'));
                    }
                });
            });
        });
    },

    show_single_task_with_comments: function (task_id, comment_id, callback) {
        this.show_single_task(task_id, comment_id, callback);
    },

    show_single_follow_up_action: function (follow_up_action_id, callback) {
        const this_ = this;

        if (!follow_up_action_id) {
            return showErrorMessage();
        }

        follow_up_action_id = parseInt(follow_up_action_id);
        showLoadingDialog();
        const follow_up_action = new FollowUpAction({ id: follow_up_action_id });
        follow_up_action.fetch({
            success: function (response) {
                hideLoadingDialog();
                this_._perform_route(
                    {
                        klass: FollowUpActionDetailsPage,
                        model: follow_up_action
                    },
                    callback
                );
            },
            error: function (error) {
                hideLoadingDialog();
                this_._perform_route(null, function () {
                    showErrorMessage(_t('mobile_message_show_single_task_error'));
                });
            }
        });
    },

    show_sensor_details: function (id, callback) {
        showLoadingDialog();
        const sensor = new Sensor({ id });
        sensor.fetch({
            use_id_param: true,
            success: () => {
                hideLoadingDialog();
                this._perform_route(
                    {
                        klass: SensorDetailPage,
                        model: sensor,
                        sparkline: new SensorSparkline({ id: id })
                    },
                    callback
                );
            },
            error: (error) => {
                hideLoadingDialog();
                this._perform_route(null, () => {
                    showErrorMessage(_t('mobile_message_show_submissions_error'));
                });
            }
        });
    },

    show_submission: function (submission_id, comment_id, callback) {
        showLoadingDialog();
        var this_ = this;
        var submission = new MobileSubmission({ id: submission_id });
        submission.fetch({
            success: function (response) {
                hideLoadingDialog();
                var expanded_metadata = response.get('expanded_metadata');

                // We'll just delete the 'expanded_metadata key so
                // that we're not passing it to the FormStructure constructor
                response.unset('expanded_metadata');
                this_._perform_route(
                    {
                        klass: SubmittedFormPage,
                        collection: response.get('form_structure'),
                        expanded_metadata: expanded_metadata,
                        active_comment_id: comment_id
                    },
                    callback
                );
            },
            error: function (error) {
                hideLoadingDialog();
                this_._perform_route(null, function () {
                    showErrorMessage(_t('mobile_message_show_submissions_error'));
                });
            }
        });
    },

    show_submission_with_comments: function (submission_id, comment_id, callback) {
        this.show_submission(submission_id, comment_id, callback);
    },

    show_account: function (account_id, active_tab_id) {
        var this_ = this;
        active_tab_id = active_tab_id || getDefaultLocationDashboardTabId();
        showLoadingDialog();
        // If the model is empty at this point we need to do the account fetch manually:

        var account = new Account({ id: account_id }); // Create a model
        account.fetch({
            success: function () {
                hideLoadingDialog();
                this_._perform_route({
                    klass: MobileDashboard,
                    initial_page_data: {
                        is_top_level_page: true,
                        active_account_or_group: account,
                        active_tab_id: active_tab_id
                    }
                });
            },
            error: function (error) {
                showErrorMessage();
                hideLoadingDialog();
            }
        });
    },

    logout: function () {
        // the next time we reload(), our zenput:// listener will be fired again and prevent us from logging in,
        // puting us in a infinite loop. So now that we've reached the #logout route, let's reset our hash to #default.
        window.location.hash = 'default';
        $f.mobile.logout();
    },

    show_forms: function () {
        this._perform_route({
            klass: FormTemplatesPage
        });
    },

    show_notification_settings: function () {
        this._perform_route({
            klass: NotificationSettings
        });
    },

    show_profile_settings: function () {
        this._perform_route({
            klass: MobileUserProfileSettings
        });
    },

    _get_all_local_records: function (callback) {
        var results = {};

        var all_records = $f.mobile.uploads.get_all_pending_local_records_classes();

        var i = 0;
        var iter = function () {
            var pending_collection = all_records[i];

            if (!pending_collection) {
                return callback(results);
            }

            pending_collection.get_all_records(function (all) {
                results[pending_collection.cache_key] = all.toJSON ? all.toJSON() : all;

                i++;
                iter();
            });
        };

        iter();
    },

    send_data: function () {
        var this_ = this;
        this._perform_action(function () {
            showLoadingDialog();
            this_._get_all_local_records(function (submissions) {
                $f.mobile.ajax({
                    url: '/logging/email/',
                    dataType: 'json',
                    type: 'POST',
                    cache: false,
                    timeout: 30000,
                    contentType: 'application/json',
                    validate: true,
                    processData: false,
                    emulateHTTP: false,
                    emulateJSON: false,
                    data: JSON.stringify(submissions),
                    success: function (response) {
                        hideLoadingDialog();
                        showSuccessMessage(_t('mobile_message_send_data_success'));
                    },
                    error: function (response) {
                        showErrorMessage();
                        hideLoadingDialog();
                    }
                });
            });
        });
    },

    enable_weinre: function () {
        enableWeinreRemoteDebug();
    },

    /**
     * We use this to redirect to a #hash after we've already redirected to that same #hash. See the below example:
     *
     *      window.locatino.hash = 'tasks';     // does what you'd expect...
     *      window.locatino.hash = 'tasks';     // when called again, we're already on #tasks from the previous line, so browser does nothing
     *
     * Unforutunatly, this still happens if you reset the URL with $f.utils.remove_url_hashbangs in between. So, we can use this noop route
     * to solve this problem:
     *
     *      window.location.hash = 'tasks';     // works the first time
     *      $f.utils.remove_url_hashbangs();    // doesn't help. browser still knows what hash we were on
     *      window.location.hash = 'noop';      // in order to go to #tasks again, we need to go away from it
     *      window.location.hash = 'tasks';     // now the browser will let us go here again
     *
     * Alternatively, we could just window.location.refresh(), but that shows a decent flicker on some devices, as it reloads the entire page.
     * @author:david
     */
    noop: function () {}
});

export default mobile;
